/**
 * Main search page component - Labelogy
 *
 * Search state persistence:
 * - Query string is stored in URL params (?q=metformin)
 * - Results are cached via React Query for 5 minutes
 * - On back navigation, URL param restores query and cache provides results
 * - State persists across hard refresh via URL params + re-fetch
 */
import { cogniEvidenceHref } from '../utils/regulatoryCitationHref';
import React, { useState, useEffect, useCallback, useContext, useMemo, useRef } from "react";
import { Link, useLocation, useNavigate, useSearchParams } from "react-router-dom";
import { auth } from "../config/firebase";
import { getImpersonationHeaders } from "../services/impersonationHeaders";
import type { WorkstreamSummary } from "../types/workstream";
import { useQueryClient } from "@tanstack/react-query";
import SearchBar from "../components/SearchBar";
import RecentSearches from "../components/RecentSearches";
import { SavedListsPanel } from "../components/SavedLists";
import type { SearchHistoryEntry, SavedStarredList } from "../types";
import { replayPath, savedListPath, handOffSavedList } from "../utils/searchNav";
import SearchFilters from "../components/SearchFilters";
import type { Corpus, SearchFilterOptions, SearchModeValue, SearchRegion } from "../types";
import {
  DEFAULT_MARKETING_CATEGORIES,
  DEFAULT_REGIONS,
  DEFAULT_SEARCH_MODE,
  DEFAULT_LETTER_TYPES,
  DEFAULT_LETTER_CORPUS,
  DEFAULT_LETTER_ROLES,
  DEFAULT_LETTER_DIVISIONS,
  normalizeSbaDocTypes,
  isUsFdaVisible,
  regionDisplayLabel,
} from "../constants/filterConstants";
import {
  withKwPage, kwSearchIdentity, deriveKwDocSources, kwConfigNeedsAdmin, coerceKwConfigForNonAdmin,
  kwRequestNeedsAdmin, sanitizeKwRequestForNonAdmin, buildKwRequest,
} from "./keywordRequest";
import type { KeywordDocSource } from "../constants/filterConstants";
import type { DrugSort } from "../utils/drugResultsTable";
import { parseChips, chipsToGroups } from "./chipUrlState";
import { parseKwInput } from '../utils/kwInputMode';
import { selectLabelSources } from "../components/KeywordSearchForm";
import { keywordRunQueryLine, keywordSearchTerms } from '../utils/keywordQueryText';
import { KW_VIEW_PARAMS } from "../utils/keywordViewParams";
import { buildCogniCompareUrl } from "../utils/cogniTableFilters";
import type { ChipKind } from "../types";
// SearchSidebar removed — the chat lives in the shell's CEPI panel
import SearchResultsTable from "../components/SearchResultsTable";
import DraftCommentsTable from "../components/DraftCommentsTable";
import BrandMark from "../components/BrandMark";
import { useSearchQueryV2Drugs } from "../hooks/useSearchQueryV2Drugs";
import { useSearchQueryV2Letters } from "../hooks/useSearchQueryV2Letters";
import { useStreamingSearch } from "../hooks/useStreamingSearch";
import { useDraftCommentsQuery } from "../hooks/useDraftCommentsQuery";
import { useDraftReviewSearch } from "../hooks/useDraftReviewSearch";
import { haDocumentsToSbaRows } from "../utils/haRunReport";
import { CI_DOCUMENT_PAGE_SIZE, useCiSearch } from "../hooks/useCiSearch";
import CiResults from "../components/CiResults";
import { useApprovalLetterSearch } from "../hooks/useApprovalLetterSearch";
import ApprovalLetterResults from "../components/ApprovalLetterResults";
import { useSearchHistory } from "../hooks/useSearchHistory";
import type { KeywordFilters } from "../components/KeywordFilterBar";
import KeywordSurface, {
  type KeywordSurfaceHandle, type KwSearchParams,
} from "../components/KeywordSurface";
import LetterResultsTable from "../components/LetterResultsTable";

import ShareButton from "../components/ShareButton";
import CogniSummaryPanel from "../components/cogni/CogniSummaryPanel";
import CogniScopeChips from "../components/cogni/CogniScopeChips";
import { useCogniSummary } from "../hooks/useCogniSummary";
import { summaryShareHtml } from "../lib/cogniSummary";
import type { CogniSummaryCite } from "../types";
import ResearchPanel from "../components/ResearchPanel";
// ``CorpusToggle`` is no longer mounted from SearchPage — the Document
// Type pill row inside ``SearchFilters`` (per-surface) is the user-
// facing corpus selector now.  The standalone component is deleted in
// Task 5 alongside the kwUrlPreserve test surgery.

import { logger } from "../lib/logger";
import { openInNewTab } from "../utils/openInNewTab";
import { useTab } from "../shell/TabContext";
import { countRows, type CogniCorpus } from "../shell/runResults";
import { ShellContext } from "../shell/shellContext";
import { stashEvidenceChunk } from "../utils/evidenceChunkHandoff";
import { coerceSearchMode } from "../utils/coerceSearchMode";
import { useDbUser } from "../hooks/useDbUser";
import { useIsAdmin } from "../hooks/useIsAdmin";
import { useHasLabelNegotiations } from "../hooks/useHasLabelNegotiations";
import { useAuth } from "../contexts/AuthContext";
import { resolveDraftTarget } from "../utils/resolveDraftTarget";
import type { DraftResultV2 } from "../types";
import { isDemoReplayEnabled, startDemoReplay } from "../demo/cepiDemoReplay";
import type {
  CommentSearchResult,
  DrugResultV2,
  KeywordSearchGroup,
  KeywordSearchRequest,
  KeywordSearchSource,
} from "../types";

// All results displayed at once (up to 50, LLM-reranked)

// Allowed values for the ``?kwSearchMode=`` URL param (Finding 1 fixup).
// Module-scope so we don't rebuild the Set on every render.  Mirrors the
// ``SearchModeValue`` union; if the union grows, add the value here too.
const VALID_KW_SEARCH_MODES: ReadonlySet<string> = new Set<SearchModeValue>([
  'labels',
  'draft_review',
  'multi_review',
  'fda_letters',
]);

// ── Top-level surface tabs (``?mode=``) ────────────────────────────────
// NOT the same axis as ``VALID_KW_SEARCH_MODES`` above: that one whitelists
// the keyword surface's DOCUMENT TYPE (``?kwSearchMode=``), while this one
// whitelists which top-level TAB is showing.  ``workstream`` is CEPI Agents'
// creation surface (plan Task 11.2, spec §3.6) and joins the union the same
// way ``researcher`` (Investigator) did.
export type SurfaceMode = 'ai' | 'keyword' | 'researcher' | 'workstream';

const VALID_SURFACE_MODES: ReadonlySet<string> = new Set<SurfaceMode>([
  'ai',
  'keyword',
  'researcher',
  'workstream',
]);

// CEPI Agents is ADMIN-ONLY while it is being validated. Gated in three
// places, because any one alone is bypassable: the tab is withdrawn once a
// non-admin is CONFIRMED, a typed ``?mode=workstream`` is coerced away here,
// and ``/workstream/:id`` is route-guarded in App.tsx. This mirrors how
// `multi_review`/`draft_review` are admin-gated in
// `filterConstants.surfaceModes`.
//
// "Confirmed", not "not yet told": the tab renders while ``/auth/sync`` is in
// flight and is removed only when it answers non-admin. Gating on the initial
// ``isAdmin: false`` instead would make an ADMIN wait a Firebase-auth + BFF +
// Cloud SQL round trip before their own tab appeared — it was the only tab in
// the bar that popped in late, which read as a lazy load. The cost is that a
// signed-in non-admin sees the tab flash; that is accepted (the tab bar is
// behind ``ProtectedRoute``, so no logged-out visitor ever sees it) because
// this is a UX gate, not a security boundary — ``/api/v2/workstreams`` is
// org-scoped server-side. See ``AdminOnlyRoute``'s docstring, which makes the
// same point for the route half.
const ADMIN_ONLY_SURFACE_MODES: ReadonlySet<string> = new Set<SurfaceMode>([
  'workstream',
]);

/** Coerce a raw ``?mode=`` value to a surface tab; Keyword is the home tab.
 *  A mode the user is not entitled to falls back to Keyword rather than
 *  rendering an empty surface. */
function coerceSurfaceMode(raw: string | null): SurfaceMode {
  if (!raw || !VALID_SURFACE_MODES.has(raw)) return 'keyword';
  return raw as SurfaceMode;
}

// ── CEPI Agents creation surface (plan Task 11.2, spec §3.6) ───────────
// Deliberately thin: the user states a request in their OWN words, we create
// the workstream, and every subsequent interaction — plan review, approval,
// progress, report — happens on the dedicated ``/workstream/:id`` page.  No
// tool, corpus or step vocabulary appears here (spec §3.2).
const WORKSTREAM_API_URL = import.meta.env['VITE_API_URL'] ||
  'https://us-central1-label-doctor-f255e.cloudfunctions.net/apiProxy';

/** Mirrors the backend's ``CreateWorkstreamRequest.request_text`` bounds. */
const WORKSTREAM_MIN_CHARS = 10;
const WORKSTREAM_MAX_CHARS = 4000;

const WORKSTREAM_STATUS_LABEL: Record<string, string> = {
  drafting: 'Drafting the plan',
  awaiting_approval: 'Awaiting your approval',
  queued: 'Queued',
  running: 'Running',
  complete: 'Complete',
  complete_with_flags: 'Complete with flags',
  failed: 'Failed',
  cancelled: 'Cancelled',
  infeasible: 'Not feasible',
};

export function WorkstreamCreatePanel({
  onCreated,
}: {
  onCreated: (id: string) => void;
}) {
  const isAdmin = useIsAdmin();
  const [text, setText] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [recent, setRecent] = useState<WorkstreamSummary[]>([]);

  const headers = useCallback(async (): Promise<Record<string, string>> => {
    const h: Record<string, string> = { 'Content-Type': 'application/json' };
    const token = await auth.currentUser?.getIdToken();
    if (token) h['Authorization'] = `Bearer ${token}`;
    Object.assign(h, getImpersonationHeaders());
    return h;
  }, []);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await fetch(`${WORKSTREAM_API_URL}/v2/workstreams?limit=20`, {
          headers: await headers(),
        });
        if (!res.ok) return;
        const body = (await res.json()) as { workstreams?: WorkstreamSummary[] };
        if (!cancelled) setRecent(body.workstreams ?? []);
      } catch {
        /* a listing failure is not a creation failure */
      }
    })();
    return () => { cancelled = true; };
  }, [headers]);

  const submit = useCallback(async () => {
    const request_text = text.trim();
    if (request_text.length < WORKSTREAM_MIN_CHARS) {
      setError(`Describe the request in at least ${WORKSTREAM_MIN_CHARS} characters.`);
      return;
    }
    if (isDemoReplayEnabled()) { onCreated(startDemoReplay()); return; }
    setBusy(true);
    setError(null);
    try {
      const res = await fetch(`${WORKSTREAM_API_URL}/v2/workstreams`, {
        method: 'POST',
        headers: await headers(),
        body: JSON.stringify(
          isUsFdaVisible(isAdmin)
            ? { request_text, page_context: { drugs_at_fda: true } }
            : { request_text },
        ),
      });
      if (!res.ok) {
        setError(`Could not start this agent run (${res.status}).`);
        return;
      }
      const body = (await res.json()) as { id?: string };
      if (body.id) onCreated(body.id);
    } catch {
      setError('Could not start this agent run.');
    } finally {
      setBusy(false);
    }
  }, [text, headers, onCreated, isAdmin]);

  return (
    <div className="max-w-3xl mx-auto px-4 py-8 space-y-6" data-testid="workstream-panel">
      <div className="space-y-2">
        <h1 className="text-xl font-semibold text-slate-900">Start an agent run</h1>
        <p className="text-sm text-slate-600">
          Describe what you need established. We will propose a plan of sub-goals for
          you to review and approve before any work runs.
        </p>
      </div>

      <div className="space-y-2">
        <label className="block text-sm text-slate-700" htmlFor="workstream-request">
          What do you need established?
        </label>
        <textarea
          id="workstream-request"
          aria-label="What do you need established?"
          className="w-full min-h-[8rem] border border-slate-300 rounded-md p-3 text-sm"
          maxLength={WORKSTREAM_MAX_CHARS}
          value={text}
          onChange={(e) => setText(e.target.value)}
          placeholder="e.g. Which EU products in this class changed section 4.4 after January 2023?"
        />
        {error && (
          <p className="text-sm text-red-700" role="alert">{error}</p>
        )}
        <button
          type="button"
          className="px-4 py-2 text-sm bg-indigo-600 text-white rounded-md disabled:opacity-50"
          disabled={busy}
          onClick={() => { void submit(); }}
        >
          {busy ? 'Starting…' : 'Propose a plan'}
        </button>
      </div>

      {recent.length > 0 && (
        <div className="space-y-2" data-testid="workstream-list">
          <h2 className="text-sm font-medium text-slate-700">Your agent runs</h2>
          <ul className="space-y-1">
            {recent.map((w) => (
              <li key={w.id}>
                <Link
                  to={`/workstream/${w.id}`}
                  className="flex items-center justify-between gap-3 border border-slate-200 rounded-md px-3 py-2 hover:bg-slate-50"
                >
                  <span className="text-sm text-slate-800 truncate">{w.request_text}</span>
                  <span className="text-xs text-slate-500 whitespace-nowrap">
                    {WORKSTREAM_STATUS_LABEL[w.status] ?? w.status}
                  </span>
                </Link>
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}

/**
 * Derive the keyword form's restoration params (groups + sources) from a
 * round-tripped ``kwConfig`` URL value, falling back to the ``kw-search-params``
 * sessionStorage snapshot when the URL carries no config (legacy path).
 *
 * This is deliberately a PURE, synchronous function of the current URL so the
 * form's ``initialGroups`` / ``initialSources`` can be computed *during render*
 * — i.e. in the same render that changes the form's ``key`` and remounts it.
 * The previous code sourced these from ``lastKwSearchParams.current``, a ref
 * only written inside the ``[searchParams]`` effect, which runs AFTER the
 * remount commits — so a replayed (Recent Search) or shared-link restore
 * remounted the form while the ref still held the previous/null value, leaving
 * the visible keyword / Exact-Near / rows / sources blank while the executed
 * search was correct.  Legacy ``rows`` payloads collapse to a single group,
 * matching the effect + ref-initializer behaviour exactly.
 */
function deriveKwSearchParams(
  kwConfig: string | null,
  storageKey: (k: string) => string,
): { groups: KeywordSearchGroup[]; sources: KeywordSearchSource[] } | null {
  if (kwConfig) {
    try {
      const config = JSON.parse(kwConfig) as KeywordSearchRequest;
      const restoredSources: KeywordSearchSource[] =
        config.sources && config.sources.length > 0 ? config.sources : ['label'];
      if (config.groups && config.groups.length > 0) {
        return { groups: config.groups, sources: restoredSources };
      }
      // Legacy URL: convert rows to single group
      if (config.rows && config.rows.length > 0) {
        return {
          groups: [{ rows: config.rows, inner_operator: config.row_operator ?? 'AND', proximity: config.proximity ?? null }],
          sources: restoredSources,
        };
      }
    } catch { /* fall through to sessionStorage */ }
  }
  const saved = sessionStorage.getItem(storageKey('kw-search-params'));
  if (!saved) return null;
  try {
    const parsed = JSON.parse(saved) as { groups?: KeywordSearchGroup[]; sources?: KeywordSearchSource[] };
    const groups = parsed.groups ?? [];
    const sources: KeywordSearchSource[] =
      parsed.sources && parsed.sources.length > 0 ? parsed.sources : ['label'];
    return { groups, sources };
  } catch { return null; }
}

const SearchPage: React.FC = () => {
  logger.debug('SearchPage rendered');

  const navigate = useNavigate();
  const location = useLocation();
  const { isActive, storageKey, reportRun, rootRef } = useTab();
  // Optional shell: tests (and storybook-like hosts) mount the page bare.
  const showRelevanceScore = useContext(ShellContext)?.showRelevanceScore ?? false;
  const [searchParams, setSearchParams] = useSearchParams();
  // B3: admin status used for coerceSearchMode in URL-sync and submit coercions.
  const isAdmin = useIsAdmin();
  // The CEPI Agents gate reads `useDbUser` and NOT the `isAdmin` above,
  // because it must tell "confirmed non-admin" from "not answered yet" —
  // `useIsAdmin()` collapses both to false (its own docstring points here for
  // exactly this case), and coercing on it would bounce a genuine admin off a
  // deep link with no way back.
  const { isAdmin: isAdminSettled, loading: adminLoading } = useDbUser();
  // Tenant owns ≥1 draft. Same role as ``isAdmin`` for the two Label
  // Negotiations modes: it gates them in every coerceSearchMode below, so a
  // stale URL cannot launch a draft search a tenant has no data for.
  const hasLabelNegotiations = useHasLabelNegotiations();
  const { user: authUser } = useAuth();
  const isAuthenticated = authUser != null;

  // ─── Top-level search mode tab ─────────────────────────────────────────
  const urlMode = searchParams.get('mode');
  // One surface per tab, read straight from the URL. Deliberately NOT
  // admin-aware: `useIsAdmin()` is false while `/auth/sync` is in flight, so
  // gating here would bounce an admin who deep-linked `?mode=workstream` and
  // never recover. The effect below coerces once the answer is actually known.
  const searchMode = coerceSurfaceMode(urlMode);

  // Admin-only surfaces: leave them once we KNOW the user is not an admin.
  // `adminLoading` is what distinguishes "not an admin" from "not yet told",
  // which `useIsAdmin()` alone cannot (see its docstring).
  useEffect(() => {
    if (adminLoading) return;
    if (!isAdminSettled && ADMIN_ONLY_SURFACE_MODES.has(searchMode)) {
      // Rewrite ``?mode=``: it IS the surface. Functional form so
      // ``searchParams`` stays out of the deps; the guard above is false on
      // the re-run, so this cannot loop.
      setSearchParams((prev) => {
        const next = new URLSearchParams(prev);
        next.set('mode', 'keyword');
        return next;
      }, { replace: true });
    }
  }, [adminLoading, isAdminSettled, searchMode, setSearchParams]);

  // Reserve the page scrollbar's space while the Keyword tab shows. Its two
  // input modes differ wildly in height (keyword results vs. Instant's empty
  // hint), so a classic (always-visible) scrollbar came and went on every
  // Keyword ⇄ Instant toggle and the centred column — search card included —
  // jumped sideways. Keyword tab only, since that toggle is where the jump
  // shows; the other tabs keep their current layout. Applied to the tab's own
  // scroll pane, which is what scrolls now.
  useEffect(() => {
    const root = rootRef.current;
    if (!root || searchMode !== 'keyword') return;
    root.style.setProperty('scrollbar-gutter', 'stable');
    return () => { root.style.removeProperty('scrollbar-gutter'); };
  }, [rootRef, searchMode]);

  // ─── Origin version scope (admin-only) ─────────────────────────────────
  // Searches each product's ORIGINAL label instead of its current one. Origin
  // and current genuinely diverge: median 3 versions / 3.7 years apart, and
  // for "pregnancy category" only 57.7% of drugs agree — the PLLR removed
  // that section from current labels entirely.
  //
  // Initial state is NOT admin-gated, for the same reason `searchMode` isn't:
  // `useIsAdmin()` is false while /auth/sync is in flight, so gating here
  // would bounce a genuine admin off a deep link with no way back. The effect
  // below coerces once the answer is actually known.
  const [isOriginScope, setIsOriginScope] = useState<boolean>(
    () => searchParams.get('is_origin') === 'true'
  );

  // Leave origin scope once we KNOW the user is not an admin. Mirrors the
  // surface-mode guard above, including its hard-won detail: rewrite the URL
  // param too, not just the state. Coercing state alone leaves
  // ``?is_origin=true`` in the URL, so every reload re-enters origin scope.
  useEffect(() => {
    if (adminLoading) return;
    if (!isAdminSettled && isOriginScope) {
      setIsOriginScope(false);
      setSearchParams((prev) => {
        const next = new URLSearchParams(prev);
        next.delete('is_origin');
        return next;
      }, { replace: true });
    }
  }, [adminLoading, isAdminSettled, isOriginScope, setSearchParams]);

  // What we ASK for. The backend re-checks platform-admin independently and
  // 403s a non-admin, so this is a UI convenience and never the boundary.
  const kwVersionScope: 'origin' | undefined = isOriginScope ? 'origin' : undefined;

  // ─── Keyword "Search in" (admin-only until K4; spec 2026-10-16 §6a) ─────
  // Same pattern as origin scope above. The selection is DERIVED from the
  // saved request (``kwConfig.sources``), never a URL param of its own
  // (plan PD11); initial state is not admin-gated for the same
  // /auth/sync reason. Rendered only on the settled admin flag (SI4).
  const [kwDocSources, setKwDocSources] = useState<KeywordDocSource[]>(
    () => deriveKwDocSources(searchParams.get('kwConfig')),
  );
  const kwConfigParam = searchParams.get('kwConfig');
  // Once we KNOW the user is not an admin, a replayed admin search (asks for
  // the document engine, or names an admin-only corpus) is reset to the
  // Labels default and its URL rewritten (plan PD10) — never converted to
  // the legacy letter mode, and a plain non-admin letter search is untouched.
  useEffect(() => {
    if (adminLoading || isAdminSettled) return;
    if (!kwConfigNeedsAdmin(kwConfigParam)) return;
    setKwDocSources(['labels']);
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      const raw = next.get('kwConfig');
      if (raw) next.set('kwConfig', coerceKwConfigForNonAdmin(raw));
      next.delete('kwSearchMode');
      return next;
    }, { replace: true });
  }, [adminLoading, isAdminSettled, kwConfigParam, setSearchParams]);

  // ─── Per-surface corpus derivation ─────────────────────────────────────
  // The Document Type pill row inside ``SearchFilters`` is the user-
  // facing corpus selector.  Each surface (keyword tab, AI/Cogni, deep
  // search) binds the pill to its own filter state and derives the
  // ``corpus`` value at the point where it's needed for dispatch — no
  // single page-level toggle and no ``?corpus=`` URL contract.  Three
  // derivation sites land below:
  //   - ``kwCorpus`` from ``kwFilters.searchMode`` for the keyword tab
  //   - ``aiPendingCorpus`` from ``pendingFilters.searchMode`` for Cogni
  //   - The Deep Search panel derives its own internally from
  //     ``filters.searchMode``; SearchPage passes nothing to it.

  // ─── Keyword Search state ──────────────────────────────────────────────
  // Restore Document Type from a dedicated ``?kwSearchMode=`` URL param
  // (Finding 1, fixup after T3+T4): without this, kwFilters always
  // initialized to ``DEFAULT_SEARCH_MODE`` ('labels'), so back-navigation
  // restoring a letter-shaped ``kwConfig`` left the visible Document Type
  // pill on Labels while the results table rendered letter rows — a
  // visible mismatch.  We use a DEDICATED key (not the existing
  // ``?searchMode=``) because that one is owned by Cogni's pendingFilters
  // / appliedFilters; sharing it would let the keyword tab clobber Cogni's
  // surface state.  ``VALID_KW_SEARCH_MODES`` lives at module scope.
  const urlKwSearchMode = searchParams.get('kwSearchMode');
  const initialKwSearchMode: SearchModeValue =
    urlKwSearchMode && VALID_KW_SEARCH_MODES.has(urlKwSearchMode)
      ? (urlKwSearchMode as SearchModeValue)
      : DEFAULT_SEARCH_MODE;
  // Restore the keyword tab's REGION selection from the round-tripped
  // ``kwConfig`` (back-nav from compare / reload / shared link). Without this,
  // ``kwFilters.regions`` always reset to DEFAULT_REGIONS (US) even when the
  // search was EU/CA/etc — so the geo selector silently flipped to US on the
  // way back from Compare. Same class of bug as the ``kwSearchMode`` "Finding 1"
  // fix above, which only covered Document Type and missed region.
  const initialKwRegions: SearchRegion[] = (() => {
    const raw = searchParams.get('kwConfig');
    if (!raw) return DEFAULT_REGIONS;
    try {
      const cfg = JSON.parse(raw) as { regions?: string[]; region?: string };
      if (Array.isArray(cfg.regions) && cfg.regions.length) return cfg.regions as SearchRegion[];
      if (cfg.region && cfg.region !== 'ALL') return [cfg.region as SearchRegion];
    } catch { /* malformed kwConfig → defaults */ }
    return DEFAULT_REGIONS;
  })();
  // Restore the Drug / Manufacturer / Pharmacological-class filters from the
  // round-tripped ``kwConfig`` at mount. Without this, a reload / shared link /
  // back-nav rehydrated the executed request (results stay filtered) while the
  // visible inputs reset to empty — the executed and visible filter states
  // diverged. Same class of bug as the region rehydration above.
  const initialKwDrugFilters = (() => {
    const empty = { drug_name: '', manufacturer: '', pharmacological_class: [] as string[] };
    const raw = searchParams.get('kwConfig');
    if (!raw) return empty;
    try {
      const cfg = JSON.parse(raw) as {
        drug_name?: string | null;
        manufacturer?: string | null;
        pharmacological_class?: string[] | null;
      };
      return {
        drug_name: typeof cfg.drug_name === 'string' ? cfg.drug_name : '',
        manufacturer: typeof cfg.manufacturer === 'string' ? cfg.manufacturer : '',
        pharmacological_class: Array.isArray(cfg.pharmacological_class)
          ? cfg.pharmacological_class : [],
      };
    } catch { return empty; }
  })();
  // Legacy ``?kwInput=<kind>`` link with no chips: row 0 starts typed as that
  // kind. ``nonce`` remounts the form (via ``formKey``) when the same mounted
  // page is navigated to such a link — the form reads this only at mount.
  // Links WITH chips are converted to a ``kwConfig`` by the restore effect.
  const [kwRowType, setKwRowType] = useState<{ type: ChipKind | undefined; nonce: number }>(() => {
    const kind = parseKwInput(searchParams.get('kwInput'));
    const typed = kind !== 'all' && kind !== 'label' && parseChips(searchParams.get('chips')).length === 0;
    return { type: typed ? kind : undefined, nonce: 0 };
  });
  // Shown when a converted chip link had to be cut to the 10-rows-per-group limit.
  const [kwMigrationNotice, setKwMigrationNotice] = useState<string | null>(null);
  // The keyword surface owns its own state now (``KeywordSurface``), so the
  // page holds a HANDLE instead of a dozen setters. Everything the URL-restore
  // effect below used to set, it now pushes through this. See the component's
  // header for why the push is imperative rather than a controlled prop.
  const kwSurfaceRef = useRef<KeywordSurfaceHandle>(null);
  const initialKwFilters: KeywordFilters = {
    drug_name: initialKwDrugFilters.drug_name,
    manufacturer: initialKwDrugFilters.manufacturer,
    pharmacological_class: initialKwDrugFilters.pharmacological_class,
    regions: initialKwRegions,
    marketingCategories: DEFAULT_MARKETING_CATEGORIES,
    searchMode: initialKwSearchMode,
    letterTypes: DEFAULT_LETTER_TYPES,
    letterCorpus: DEFAULT_LETTER_CORPUS,
    letterRoles: DEFAULT_LETTER_ROLES,
    letterDivisions: DEFAULT_LETTER_DIVISIONS,
  };
  // (``kwOtherDocs`` lived here — a mirror of the keyword form's Labels /
  // Other-Documents mode, used to hide the region-scoped filter controls that
  // were inert in that mode. Removed 2026-07-29 with the mode itself; every
  // keyword search is now region-scoped, so those controls always apply.)
  // ``sectionOptions`` moved into KeywordSurface with the form that uses it.
  // KW_PAGE_SIZE / isSingleExactPhrase / buildKwRequest live in
  // ./keywordRequest (extracted so buildKwRequest is unit-testable).

  // Restore keyword search state from URL params (enables back-navigation)
  const urlKwConfig = searchParams.get('kwConfig');
  const urlKwPage = searchParams.get('kwPage');
  // Mount-time restore: URL first, then this tab's sessionStorage as the
  // legacy fallback. The surface (mounted only in keyword mode) takes these as
  // its initial state; every LATER restore arrives through ``kwSurfaceRef``.
  //
  // Sources are passed through verbatim — Phase G Task 13 wired the letter
  // corpus, so the discriminated response handles letter-source results
  // natively and the previous ``sanitizeKwSources`` defensive guard is gone.
  // The server sort persisted next to kw-request (AC16 Back). It belongs to one
  // search: a request with a different identity ignores it (P10).
  const readKwSort = (request: KeywordSearchRequest): DrugSort | null => {
    try {
      const raw = sessionStorage.getItem(storageKey('kw-sort'));
      if (!raw) return null;
      const saved = JSON.parse(raw) as { search?: string; sort?: DrugSort | null };
      return saved.search === kwSearchIdentity(request) ? saved.sort ?? null : null;
    } catch { return null; }
  };
  const initialKw = useMemo<{
    page: number;
    request: KeywordSearchRequest | null;
    params: KwSearchParams | null;
    sort: DrugSort | null;
  }>(() => {
    const page = urlKwPage
      ? Number(urlKwPage)
      : Number(sessionStorage.getItem(storageKey('kw-page')) ?? '1');
    const request = (() => {
      // Priority 1: URL params (back-navigation / shared link)
      if (urlKwConfig) {
        try {
          return withKwPage(JSON.parse(urlKwConfig) as KeywordSearchRequest,
            urlKwPage ? Number(urlKwPage) : 1);
        } catch { /* fall through */ }
      }
      // Priority 2: sessionStorage (legacy fallback)
      const saved = sessionStorage.getItem(storageKey('kw-request'));
      if (!saved) return null;
      try {
        return withKwPage(JSON.parse(saved) as KeywordSearchRequest,
          Number(sessionStorage.getItem(storageKey('kw-page')) ?? '1'));
      } catch { return null; }
    })();
    // A SETTLED non-admin never restores an admin-only request (PD10): it is
    // sanitized here, synchronously, before the surface can run it.
    const settledNonAdmin = !adminLoading && !isAdminSettled;
    const safeRequest = request && settledNonAdmin && kwRequestNeedsAdmin(request)
      ? sanitizeKwRequestForNonAdmin(request) : request;
    return { page: Number.isFinite(page) && page >= 1 ? page : 1, request: safeRequest,
             params: deriveKwSearchParams(urlKwConfig, storageKey),
             sort: request ? readKwSort(request) : null };
    // Recomputed only on entering keyword mode (the surface mounts then and
    // reads this once); keyword mode is the only time it is consumed.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchMode === 'keyword']);
  // Synchronous, render-time restoration source for the form's ``initialGroups``
  // / ``initialSources``.  Unlike ``lastKwSearchParams.current`` (a ref written
  // only inside the ``[searchParams]`` effect, which runs AFTER the form's key
  // change remounts it), this is recomputed during render from the current URL,
  // so a replayed / shared-link restore populates the form the same tick it
  // remounts.  Recompute only when the URL changes.
  const restoredKwSearchParams = useMemo(
    () => deriveKwSearchParams(searchParams.get('kwConfig'), storageKey),
    [searchParams, storageKey],
  );
  // Bumped on every Recent-Search replay so re-clicking the SAME entry (whose
  // URL is unchanged, so ``kwConfig`` in the key doesn't move) still forces a
  // remount and re-restores the form, discarding any in-progress edits.
  const [kwReplayNonce, setKwReplayNonce] = useState(0);

  // Helper to persist keyword search state to URL params
  // replace=true for page changes (don't pollute history), replace=false for new searches (enable back-nav)
  //
  // Uses the functional ``setSearchParams(prev => …)`` form so we
  // atomically read-modify-write the current URL params and PRESERVE
  // unrelated params (search mode, AI query state, etc.).  Rebuilding
  // via ``new URLSearchParams()`` would wipe those, breaking cross-tab
  // state preservation.
  // ``filters`` is a PARAMETER, not closure state: the surface owns the filter
  // state now and hands the exact values that produced the request, so this
  // can no longer serialize a stale searchMode (the "Finding 1" bug class) and
  // no longer needs kwFilters in a dep array.  ``KeywordSearchRequest`` is the
  // network shape and doesn't carry ``searchMode`` — corpus is derived
  // server-side from the ``sources`` array — so we write the UI-state value.
  // The URL persistence point is search submit / pagination, not pill click;
  // mid-edit changes don't write to the URL (matches the existing UX rhythm).
  const updateKwUrlParams = useCallback((
    request: KeywordSearchRequest, page: number, kwFilters: KeywordFilters, replace = false,
  ) => {
    setSearchParams(
      (prev) => {
        const params = new URLSearchParams(prev);
        params.set('mode', 'keyword');
        params.set('kwConfig', JSON.stringify(request));
        if (page > 1) {
          params.set('kwPage', String(page));
        } else {
          // Keep the URL clean on page 1 (default).
          params.delete('kwPage');
        }
        // Document Type round-trip: write when non-default, strip when
        // default.  Default state never appears in the URL.
        if (kwFilters.searchMode !== DEFAULT_SEARCH_MODE) {
          params.set('kwSearchMode', kwFilters.searchMode);
        } else {
          params.delete('kwSearchMode');
        }
        // Persist the FRONTEND marketing-category selection (Application Type
        // pills) so back-nav from Compare restores it. kwConfig only carries
        // the lossy backend codes (`marketing_category`) — and eu/uk_authorised
        // both map to "Authorised", so it can't be reversed — hence its own
        // param holding the exact frontend values.
        params.set('kwCats', kwFilters.marketingCategories.join(','));
        return params;
      },
      { replace },
    );
  }, [setSearchParams]);

  // One handler for both "a search ran" and "the page changed" — the surface
  // reports them the same way and only ``replace`` differs (paging must not
  // push a history entry). This is now the ONLY place keyword state reaches
  // the URL and sessionStorage.
  const handleKwSearchExecuted = useCallback(
    (e: {
      request: KeywordSearchRequest; page: number;
      filters: KeywordFilters; params: KwSearchParams; replace: boolean;
      sort: DrugSort | null;
    }) => {
      if (!e.replace) setKwMigrationNotice(null);
      // Per-tab keys: two Keyword tabs must not restore each other's search.
      // Best effort: per-tab keys add up, and a quota error must not stop the
      // URL update below (the tab's own record of the search).
      try {
        sessionStorage.setItem(storageKey('kw-search-params'), JSON.stringify(e.params));
        sessionStorage.setItem(storageKey('kw-page'), String(e.page));
        sessionStorage.setItem(storageKey('kw-request'), JSON.stringify(e.request));
        if (e.sort) {
          sessionStorage.setItem(storageKey('kw-sort'), JSON.stringify(
            { search: kwSearchIdentity(e.request), sort: e.sort }));
        } else {
          sessionStorage.removeItem(storageKey('kw-sort'));
        }
      } catch {
        /* sessionStorage full or unavailable; the URL still carries the search */
      }
      updateKwUrlParams(e.request, e.page, e.filters, e.replace);
    },
    [updateKwUrlParams, storageKey],
  );

  // ─── AI Search state (existing) ────────────────────────────────────────

  // Initialize state from URL params (enables persistence across navigation/refresh)
  const urlQuery = searchParams.get('q') || '';
  const urlCategories = searchParams.get('categories')
    ? searchParams.get('categories')!.split(',').filter(Boolean)
    : DEFAULT_MARKETING_CATEGORIES;
  // Backward compat: read legacy ?region= (singular) and ?draftReview/?sba params
  const urlRegions: SearchRegion[] = searchParams.get('regions')
    ? (searchParams.get('regions')!.split(',').filter(Boolean) as SearchRegion[])
    : searchParams.get('region') && searchParams.get('region') !== 'ALL'
      ? [searchParams.get('region') as SearchRegion]
      : DEFAULT_REGIONS;
  const urlSearchMode: SearchModeValue =
    (searchParams.get('searchMode') as SearchModeValue) ??
    (searchParams.get('sba') === 'true' ? 'multi_review' :
     searchParams.get('draftReview') === 'true' ? 'draft_review' : DEFAULT_SEARCH_MODE);

  // FDA-letter filter URL persistence: ``letterTypes`` / ``letterCorpus``
  // / ``letterRoles`` round-trip through the URL so refresh / back / share
  // preserves a non-default pill selection. An ABSENT param falls back to
  // the default (most users); an EXPLICIT empty string maps to ``[]`` (no
  // constraint for that dimension) — these two states are intentionally
  // distinct because ``[]`` means "all pills off = no filter" while the
  // default is "the canonical defaults pre-selected".
  const parseUrlLetterArray = (key: string, fallback: string[]): string[] => {
    const raw = searchParams.get(key);
    if (raw === null) return fallback;
    if (raw === '') return [];
    return raw.split(',').filter(Boolean);
  };
  const urlLetterTypes = parseUrlLetterArray('letterTypes', DEFAULT_LETTER_TYPES);
  const urlLetterCorpus = parseUrlLetterArray('letterCorpus', DEFAULT_LETTER_CORPUS);
  const urlLetterRoles = parseUrlLetterArray('letterRoles', DEFAULT_LETTER_ROLES);
  const urlLetterDivisions = parseUrlLetterArray('letterDivisions', DEFAULT_LETTER_DIVISIONS);
  // Cogni HA review subtype URL state is always canonical and non-empty.
  // Missing/legacy and fully-invalid values intentionally become the
  // frontend's Medical Review default; the backend never supplies this
  // default implicitly.
  const rawUrlSbaDocTypes = searchParams.get('sbaDocTypes');
  const urlSbaDocTypes = normalizeSbaDocTypes(
    rawUrlSbaDocTypes === null ? undefined : rawUrlSbaDocTypes.split(','),
  );
  // Guard URL-derived mode state before the first committed render. The
  // approval-search hook is declared before the later back/forward sync
  // effect, so deferring this coercion to that effect can launch one request
  // from a flag-off, non-US, or multi-region stale URL.
  const coercedUrlFilters = coerceSearchMode({
    marketingCategories: urlCategories,
    regions: urlRegions,
    searchMode: urlSearchMode,
    sbaDocTypes: [...urlSbaDocTypes],
    letterTypes: urlLetterTypes,
    letterCorpus: urlLetterCorpus,
    letterRoles: urlLetterRoles,
    letterDivisions: urlLetterDivisions,
  }, 'cogni', isAdmin, hasLabelNegotiations);

  // Label Negotiations: additive URL param that keeps searchMode='labels'
  // and enables draft-corpus results alongside regular Cogni search.
  const urlLabelNegotiations = searchParams.get('labelNegotiations') === 'true';
  const [rawIncludeLabelNegotiations, setIncludeLabelNegotiations] = useState<boolean>(urlLabelNegotiations);
  // EFFECTIVE value. The raw state is restored verbatim from the URL, so a
  // saved ``?labelNegotiations=true`` link (or a capability sync that failed
  // closed) would otherwise send ``include_label_negotiations=true`` and render
  // a checked-but-disabled box for a tenant with no drafts. Gating only the
  // input's ``disabled`` attribute does not stop the request — this does.
  // Mirrors what coerceSearchMode does for the two pill modes.
  const includeLabelNegotiations = rawIncludeLabelNegotiations && hasLabelNegotiations;
  // Comments toggle: 'all' (draft content + comments) | 'comments_only'.
  // Matches the backend's two-value ``draft_comment_scope`` vocabulary — the
  // backend does the scoping (gate: run the content arm only when scope is not
  // 'comments_only'), so the FE just renders whatever it returns.
  const [draftCommentScope, setDraftCommentScope] = useState<'all' | 'comments_only'>('all');

  const [searchQuery, setSearchQuery] = useState<string>(urlQuery);
  const [topBarQuery, setTopBarQuery] = useState<string>(urlQuery);
  const [activeResultTab, setActiveResultTab] = useState<'drugs' | 'comments'>('drugs');

  // Pending filters: UI state that changes on toggle (doesn't trigger search)
  const [pendingFilters, setPendingFilters] = useState<SearchFilterOptions>(
    () => ({ ...coercedUrlFilters }),
  );
  // Applied filters: sent to backend when user clicks search.  Letter
  // arrays gate on ``urlQuery`` the same way categories/regions do so a
  // bare ``/`` load presents defaults rather than honouring a stale
  // ``?letterTypes=…`` that has no accompanying query.
  const [appliedFilters, setAppliedFilters] = useState<SearchFilterOptions>(
    () => urlQuery
      ? { ...coercedUrlFilters }
      : {
          marketingCategories: DEFAULT_MARKETING_CATEGORIES,
          regions: DEFAULT_REGIONS,
          searchMode: DEFAULT_SEARCH_MODE,
          sbaDocTypes: [...urlSbaDocTypes],
          letterTypes: DEFAULT_LETTER_TYPES,
          letterCorpus: DEFAULT_LETTER_CORPUS,
          letterRoles: DEFAULT_LETTER_ROLES,
          letterDivisions: DEFAULT_LETTER_DIVISIONS,
        },
  );

  // Derived from appliedFilters.searchMode (used throughout rendering)
  const appliedIncludeComments = appliedFilters.searchMode === 'draft_review';
  const appliedIncludeSba = appliedFilters.searchMode === 'multi_review';
  // Plan-4 Task 5: competitive-intel (CI) corpus — company product pages +
  // press releases. Region-agnostic, dispatches to useCiSearch below
  // instead of the drug-label search hooks.
  const appliedIncludeCi = appliedFilters.searchMode === 'ci';
  const appliedIncludeApprovalLetters = appliedFilters.searchMode === 'approval_letters';
  const isNormalSearchMode = appliedFilters.searchMode === 'labels';
  // multi_review (HA reviews) now runs through the same Cogni drug pipeline as
  // labels, with corpus="ha_reviews". Label-only surfaces keep isNormalSearchMode.
  const isCogniDrugMode = isNormalSearchMode || appliedIncludeSba;
  const haCorpusOptions = appliedIncludeSba
    ? {
        corpus: 'ha_reviews' as const,
        regions: [isUsFdaVisible(isAdmin) ? 'US_FDA' : 'US'],
        review_doc_types: normalizeSbaDocTypes(appliedFilters.sbaDocTypes),
      }
    : null;
  const haDocTypesKey = haCorpusOptions ? [...haCorpusOptions.review_doc_types].sort().join(',') : '';

  // Per-surface corpus derivation (Task 3): the AI / Cogni surface binds
  // its Document Type pill to ``pendingFilters.searchMode``.  The derived
  // corpus is threaded into both the hero and results-top-bar
  // ``SearchFilters`` mounts so the drug-only sub-rows hide when the
  // user selects FDA Letters (or, as of Task 5, Competitive Intel).
  const aiPendingCorpus: Corpus =
    pendingFilters.searchMode === 'fda_letters' ? 'fda_letters'
      : pendingFilters.searchMode === 'ci' ? 'ci'
      : pendingFilters.searchMode === 'approval_letters' ? 'approval_letters'
      : 'labels';

  // Pagination state for V2 drug search (restored from URL on back navigation)
  const urlPage = parseInt(searchParams.get('page') || '1', 10);
  const [currentPage, setCurrentPage] = useState(urlPage || 1);

  // Search history management (per-user; records the full replayable request).
  const { recordSearch } = useSearchHistory();
  // Guards record-on-completion so each distinct search is logged once.
  const lastRecordedRef = useRef<string>('');

  // Determine if we should show sidebar layout (after first search)
  const hasSearched = urlQuery.length > 0;

  // Auto-grow the results-page top-bar search box to fit the current query.
  // The inline onChange handler only resizes while the user types; this effect
  // also resizes when the query is set programmatically (URL restore, a new
  // search) and when the results layout first mounts — so a long query shows
  // in full without the user having to click into the box and type.
  const topBarTextareaRef = useRef<HTMLTextAreaElement | null>(null);
  // Bumped on every Ask so re-submitting unchanged inputs re-streams page 1 and resets the summary.
  const [submitGen, setSubmitGen] = useState(0);
  const [filtersOpen, setFiltersOpen] = useState(false);
  useEffect(() => {
    const el = topBarTextareaRef.current;
    if (!el) return;
    el.style.height = 'auto';
    el.style.height = `${el.scrollHeight}px`;
  }, [topBarQuery, searchMode, hasSearched]);

  // Streaming SSE search for page 1 — results arrive progressively as reranker batches complete.
  // Two-stream architecture: backend pipeline writes to Redis independently of the SSE connection.
  // On back-navigation: backend serves cached results from Redis as fast SSE (~100ms).
  // Always stream for page 1 — no need for partial/complete cache distinction on the frontend.
  const queryClient = useQueryClient();

  const categoriesKey = appliedFilters.marketingCategories.join(',');
  // Sorted-and-joined regions for stable query keys (toggle order doesn't
  // fragment cache between equivalent selections).
  const regionsKey = [...appliedFilters.regions].sort().join(',');
  const page1QueryKey = useMemo(() => ['search-v2-drugs', searchQuery, {
    top_k: 50, drug_filter: undefined, section_filter: undefined,
    marketingCategories: appliedFilters.marketingCategories,
    regionsKey, page: 1, page_size: 50,
    corpus: appliedIncludeSba ? 'ha_reviews' : 'labels', haDocTypesKey,
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }], [searchQuery, categoriesKey, regionsKey, appliedIncludeSba, haDocTypesKey]);

  const useStreaming = isCogniDrugMode && currentPage === 1;

  const {
    data: streamingResults,
    isLoading: isStreamingLoading,
    error: streamingError,
    drugCount: streamingDrugCount,
  } = useStreamingSearch(searchQuery, {
    marketingCategories: appliedFilters.marketingCategories,
    regions: appliedFilters.regions,
    ...(haCorpusOptions ?? {}),
    ...(!haCorpusOptions && includeLabelNegotiations ? { include_label_negotiations: true } : {}),
    ...(draftCommentScope !== 'all' ? { draft_comment_scope: draftCommentScope } : {}),
    submitGen,
  }, useStreaming);

  // Populate React Query cache from streaming results so pages 2+ work from RQ cache.
  // Only cache complete results (latency_ms > 0 from the "done" event).
  useEffect(() => {
    if (streamingResults && (streamingResults.metadata?.latency_ms ?? 0) > 0) {
      queryClient.setQueryData(page1QueryKey, streamingResults);
    }
  }, [streamingResults, queryClient, page1QueryKey]);

  // React Query for pages 2+ (served from Redis permanent cache)
  const {
    data: paginatedResults,
    isLoading: isPaginatedLoading,
    error: paginatedError,
  } = useSearchQueryV2Drugs(searchQuery, {
    top_k: 50,
    marketingCategories: appliedFilters.marketingCategories,
    regions: appliedFilters.regions,
    ...(haCorpusOptions ?? {}),
    page: currentPage,
    page_size: 50,
    // Include searchMode in the cache key so the labels-mode cache doesn't
    // collide with a same-query letters-mode caller (defense in depth — the
    // hook itself is disabled in non-labels mode via ``isNormalSearchMode``,
    // but the cached value from a prior labels submission can still flow
    // through ``v2Results`` if the key matches.  Also gated below at the
    // ``searchResults`` derivation for render-time defense.)
    searchMode: appliedFilters.searchMode,
    ...(!haCorpusOptions && includeLabelNegotiations ? { include_label_negotiations: true } : {}),
    ...(draftCommentScope !== 'all' ? { draft_comment_scope: draftCommentScope } : {}),
  }, isCogniDrugMode && currentPage > 1);

  // Streaming for page 1, React Query for pages 2+
  const v2Results = useStreaming ? streamingResults : paginatedResults;
  const isSearchLoading = useStreaming ? isStreamingLoading : isPaginatedLoading;
  const queryError = useStreaming ? streamingError : paginatedError;

  // Cogni FDA letter AI search (Task 3): when ``appliedFilters.searchMode``
  // is ``'fda_letters'``, this hook fires alongside the drug hooks (which
  // are gated off in letter mode via ``isNormalSearchMode``).  The dedicated
  // hook + route mirrors the SBA / draft-review pattern — no ``corpus``
  // discriminator on the shared drug schema.
  //
  //   - ``isLetterMode``       — single derived predicate referenced
  //     across the widened ``isLoading`` / ``error`` / outer-gate
  //     blocks and the new letter-mode render branch.
  //   - ``hasLetterActivity``  — opens the outer results-section gate
  //     when the letter hook is loading, has data, OR has errored.
  //     Loading + error MUST be included; otherwise the row-14 inline
  //     "Searching FDA letters…" indicator and the row-6 error banner
  //     would be unreachable (the gate would only open on resolved
  //     data).
  const isLetterMode = appliedFilters.searchMode === 'fda_letters';
  // Task 8: attach the three letter-corpus filter arrays when the
  // applied search mode is FDA Letters.  Snake_case mirrors the
  // backend ``LetterAISearchRequest`` wire format.  When the mode is
  // anything else, ``isLetterMode`` is false and the hook short-
  // circuits before sending — passing ``undefined`` keeps the filter
  // payload off the request entirely.
  const letterSearchQuery = useSearchQueryV2Letters(
    searchQuery,
    isLetterMode,
    isLetterMode
      ? {
          letter_types: appliedFilters.letterTypes,
          letter_corpus: appliedFilters.letterCorpus,
          letter_roles: appliedFilters.letterRoles,
          letter_divisions: appliedFilters.letterDivisions,
        }
      : undefined,
  );
  const hasLetterActivity =
    isLetterMode && (letterSearchQuery.isLoading || !!letterSearchQuery.data || !!letterSearchQuery.error);

  // On-request Cogni summary (R7/R8): the state machine lives in the hook.
  const cogniCacheKey = v2Results?.metadata?.cache_key;
  const summary = useCogniSummary({
    cacheKey: cogniCacheKey,
    resetKey: `${submitGen}|${searchQuery}|${JSON.stringify(appliedFilters)}|${includeLabelNegotiations}`,
    streamLoading: useStreaming && isStreamingLoading,
  });

  // Store V2 results directly - no adapter transformation needed
  //
  // Render-time defense for the cache-collision bug fixed at the hook layer:
  // ``v2Results`` can still carry a value when ``appliedFilters.searchMode``
  // is anything other than ``'labels'`` (e.g., paginatedResults from a prior
  // labels submission persists in the React Query cache).  Gating the
  // derivation on ``searchMode === 'labels'`` ensures ``searchResults`` /
  // ``currentResults`` stay null in letter mode regardless of cache state,
  // so the share button, chat rail, and other drug-only surfaces (which gate
  // on ``currentResults.length > 0``) don't accidentally surface with stale
  // drug context.
  const searchResults = useMemo(() => {
    if (!v2Results) return null;
    if (!isCogniDrugMode) return null;
    return {
      intent: v2Results.intent?.semantic_query || '',
      results: v2Results.drug_results,  // Direct V2 results
      total_candidates: v2Results.metadata?.total_candidates || 0,
      cached: false,
      // V2 API doesn't support comment search - these are handled by Draft Review Mode
      comment_results: undefined as CommentSearchResult[] | undefined,
      total_comment_matches: undefined as number | undefined,
    };
  }, [v2Results, isCogniDrugMode]);

  // Draft Review Mode: use intelligent search when query present, fallback to basic fetch
  const hasSearchQueryForDraft = appliedIncludeComments && searchQuery.trim().length > 0;

  // Intelligent draft review search (when query is present)
  const {
    data: draftReviewData,
    isLoading: isDraftReviewLoading,
    refetch: refetchDraftReview,
  } = useDraftReviewSearch(
    { query: searchQuery },
    hasSearchQueryForDraft // Only enabled when in Draft Review Mode AND has search query
  );

  // Basic draft comments fetch (when no query - show all)
  const {
    data: draftCommentsData,
    isLoading: isDraftCommentsLoading,
    refetch: refetchDraftComments,
  } = useDraftCommentsQuery({}, appliedIncludeComments && !hasSearchQueryForDraft);

  const hasSbaQuery = appliedIncludeSba && searchQuery.trim().length > 0;

  // Competitive-Intel (CI) Search: one-shot POST /api/ci/search (Task 5).
  const hasCiQuery = appliedIncludeCi && searchQuery.trim().length > 0;
  const {
    documents: ciDocuments,
    isLoading: isCiSearchLoading,
    totalDocuments: ciTotalDocuments,
  } = useCiSearch(
    searchQuery,
    hasCiQuery,
    currentPage,
    CI_DOCUMENT_PAGE_SIZE,
  );

  const hasApprovalLetterQuery =
    appliedIncludeApprovalLetters && searchQuery.trim().length > 0;
  const {
    results: approvalLetterResults,
    isLoading: isApprovalLetterSearchLoading,
    error: approvalLetterSearchError,
  } = useApprovalLetterSearch(
    searchQuery,
    appliedFilters.marketingCategories,
    hasApprovalLetterQuery,
    isUsFdaVisible(isAdmin) ? 'US_FDA' : undefined,
  );

  // Combine loading states
  const isDraftLoading = hasSearchQueryForDraft ? isDraftReviewLoading : isDraftCommentsLoading;
  // Letter mode widens the combined loading flag so the SearchBar /
  // SearchFilters compact controls disable while the Cogni FDA letter
  // hook is in flight.  This does NOT light up the drug-streaming page-
  // level spinners (those stay gated on ``useStreaming && isStreamingLoading``);
  // the letter-mode inline indicator lives in the results column below.
  const isLoading = appliedIncludeApprovalLetters
    ? isApprovalLetterSearchLoading
    : isLetterMode
    ? letterSearchQuery.isLoading
    : appliedIncludeComments ? isDraftLoading
    : appliedIncludeCi ? isCiSearchLoading
    : isSearchLoading;

  // Get the appropriate draft results based on whether we have a search query
  const draftResults = hasSearchQueryForDraft
    ? draftReviewData?.results || []
    : draftCommentsData?.results || [];

  // Refetch handler for draft mode
  const handleDraftRefetch = hasSearchQueryForDraft ? refetchDraftReview : refetchDraftComments;

  // Convert React Query error to string for display.
  //
  // Letter mode (Task 3) sources from ``letterSearchQuery.error`` instead
  // of the drug ``queryError`` so the same page-level error banner
  // surfaces a failed Cogni FDA letter search.  Format the message the
  // same way: ``.response?.data?.detail`` (axios shape) then
  // ``.message`` fallback.
  const letterError = isLetterMode ? letterSearchQuery.error : null;
  const activeError = appliedIncludeApprovalLetters
    ? approvalLetterSearchError
    : isLetterMode ? letterError : queryError;
  const error = activeError ? (
    (activeError as any)?.response?.data?.detail ||
    (activeError as Error)?.message ||
    "An error occurred while searching"
  ) : null;

  // Sync URL params when search query and filters change.
  //
  // Uses the functional ``setSearchParams(prev => …)`` form so unrelated
  // params managed elsewhere (e.g. keyword config) survive an AI search
  // submission.  Document Type is serialized via ``searchMode`` here.
  const updateUrlParams = useCallback((query: string, filters: SearchFilterOptions, page: number = 1, labelNeg?: boolean) => {
    setSearchParams(
      (prev) => {
        const params = new URLSearchParams(prev);
        // Reset AI-search-managed keys, then re-populate from arguments.
        params.delete('q');
        params.delete('searchMode');
        params.delete('categories');
        params.delete('regions');
        params.delete('region');
        params.delete('page');
        params.delete('letterTypes');
        params.delete('letterCorpus');
        params.delete('letterRoles');
        params.delete('letterDivisions');
        params.delete('sbaDocTypes');
        params.delete('labelNegotiations');
        if (query) params.set('q', query);
        if (filters.searchMode !== 'labels') params.set('searchMode', filters.searchMode);
        if (filters.marketingCategories.length > 0) params.set('categories', filters.marketingCategories.join(','));
        if (filters.regions.length > 0) params.set('regions', filters.regions.join(','));
        if (page > 1) params.set('page', String(page));
        // FDA-letter pill state: always serialise when in letter mode so
        // empty arrays (no constraint) round-trip faithfully. Setting an
        // empty string for an explicitly-cleared dimension is distinct
        // from omitting the param (which falls back to defaults on read).
        if (filters.searchMode === 'fda_letters') {
          params.set('letterTypes', filters.letterTypes.join(','));
          params.set('letterCorpus', filters.letterCorpus.join(','));
          params.set('letterRoles', filters.letterRoles.join(','));
          params.set('letterDivisions', filters.letterDivisions.join(','));
        }
        // Always persist an explicit canonical type set for multi-review,
        // including the one-item Medical default. Other modes remove the
        // parameter while retaining the in-memory pending selection.
        if (filters.searchMode === 'multi_review') {
          params.set(
            'sbaDocTypes',
            normalizeSbaDocTypes(filters.sbaDocTypes).join(','),
          );
        }
        // Label Negotiations: additive flag, only set when enabled AND the
        // tenant actually has drafts — otherwise a no-op flag would be written
        // back into the URL and re-restored on every navigation.
        const ln = (labelNeg ?? includeLabelNegotiations) && hasLabelNegotiations;
        if (ln && filters.searchMode === 'labels') params.set('labelNegotiations', 'true');
        return params;
      },
      { replace: true },
    );
  }, [setSearchParams, includeLabelNegotiations, hasLabelNegotiations]);

  // Handle new search from SearchBar
  // When search is triggered, apply pending filters to the actual search
  const handleHybridSearch = useCallback((query: string) => {
    // History is recorded on completion (so it captures result_count + the
    // final URL for replay) — see the record-on-completion effects below.
    setSubmitGen((g) => g + 1);
    setSearchQuery(query);
    setTopBarQuery(query);
    setActiveResultTab('drugs');
    setCurrentPage(1); // Reset pagination on new search
    // B3: coerce searchMode at submit time in case a stale/incompatible
    // mode slipped through (e.g. multi_review with regions != ['US']).
    const coercedBase = coerceSearchMode(pendingFilters, 'cogni', isAdmin, hasLabelNegotiations);
    const coerced: SearchFilterOptions = {
      ...coercedBase,
      sbaDocTypes: normalizeSbaDocTypes(coercedBase.sbaDocTypes),
    };
    setAppliedFilters(coerced);
    updateUrlParams(query, coerced, 1);
    logger.info('Search initiated', { query, filters: coerced });
  }, [updateUrlParams, pendingFilters, isAdmin, hasLabelNegotiations]);

  const handleToggleLabelNegotiations = useCallback((checked: boolean) => {
    setIncludeLabelNegotiations(checked);
    if (searchQuery) updateUrlParams(searchQuery, appliedFilters, currentPage, checked);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchQuery, appliedFilters, currentPage]);

  // ── Record searches into per-user history on completion ──────────────────
  // Recording here (not at trigger) captures the result count and the final
  // URL, which is exactly what replay navigates to. Gated to page 1 so
  // pagination doesn't create duplicate entries; the backend also dedupes.
  useEffect(() => {
    if (searchMode !== 'ai') return;
    // A background tab's search is not something the user just did; its URL
    // is also not the one on screen.
    if (!isActive) return;
    if (currentPage !== 1) return;
    const q = (searchQuery || '').trim();
    if (!q) return;
    // FDA-letter mode runs a SEPARATE query (letterSearchQuery) — the drug
    // hooks that feed v2Results are gated off in letter mode (isNormalSearchMode
    // === 'labels'). So gate on the right results source per corpus; otherwise
    // a letter search never records (it bails on the empty v2Results), or
    // records a stale drug count from a prior labels search. (multi_review /
    // draft_review keep gating on v2Results — unchanged.)
    const isLetter = appliedFilters.searchMode === 'fda_letters';
    const ready = isLetter
      ? !letterSearchQuery.isLoading && !!letterSearchQuery.data
      : !isSearchLoading && !!v2Results;
    if (!ready) return;
    const url = location.pathname + location.search;
    const key = `main|${url}`;
    if (lastRecordedRef.current === key) return;
    lastRecordedRef.current = key;
    const resultCount = isLetter
      ? letterSearchQuery.data?.results.length
      : v2Results?.metadata?.total_drugs;
    recordSearch({
      mode: isLetter ? 'letter' : 'main',
      label: q,
      payload: {
        url,
        query: q,
        regions: appliedFilters.regions,
        marketingCategories: appliedFilters.marketingCategories,
        searchMode: appliedFilters.searchMode,
        // Cogni FDA-letter searches carry Letter Type/Corpus/Role/Division
        // filters (sent at request time). Record them so they survive the
        // url-free dedup fingerprint and distinct letter searches stay distinct.
        ...(isLetter
          ? {
              letter_types: appliedFilters.letterTypes,
              letter_corpus: appliedFilters.letterCorpus,
              letter_roles: appliedFilters.letterRoles,
              letter_divisions: appliedFilters.letterDivisions,
            }
          : {}),
      },
      ...(resultCount != null ? { result_count: resultCount } : {}),
    });
  }, [
    searchMode, currentPage, searchQuery, isSearchLoading, v2Results, isActive, location,
    letterSearchQuery.isLoading, letterSearchQuery.data, appliedFilters, recordSearch,
  ]);

  // Recent Searches. Was an effect watching the keyword query; the surface
  // owns that query now and calls this once per page-1 result set, with the
  // filters and groups that actually produced it — so the recorded payload
  // can no longer drift from the executed search.
  const lastCogniSigRef = useRef<string | null>(null);
  const lastKwReportRef = useRef<{ data: unknown } | null>(null);
  const handleKwResults = useCallback((e: {
    data: unknown; page: number; filters: KeywordFilters; params: KwSearchParams;
    request: KeywordSearchRequest;
  }) => {
    const data = e.data as
      | { kind?: string; total_count?: number; total_drugs?: number; results?: unknown[] }
      | undefined;
    if (!data) return;
    // Register the run with the shell. Every page is reported: the run is the
    // page on screen, so "Answer from…" on page 3 attaches page 3. ZERO ROWS ARE REPORTED, unlike Cogni: a
    // complete census that matched nothing is a real finding ("nothing in the
    // corpus says this"). Ranked discovery's zero proves nothing.
    // The surface re-fires this on re-activation and on paging back to a page;
    // the same response is the same run (react-query hands back the cached
    // object even though paging rebuilds an equal-but-fresh request), and a fresh `seq` would drop the
    // chat's "Added" state and allow a duplicate attach.
    const last = lastKwReportRef.current;
    if (!last || last.data !== e.data) {
      lastKwReportRef.current = { data: e.data };
      reportRun({
        kind: 'set',
        capability: 'keyword',
        count: countRows('keyword', e.data),
        queryText: keywordSearchTerms(e.params.groups).join(' '),
        // The QUERY line the page shows.
        searchQuery: keywordRunQueryLine(e.params.groups, e.filters, e.request),
        request: e.request,
        response: e.data,
      });
    }
    // Page 2 of a search is the same search. History records it once.
    if (e.page !== 1) return;
    // Only the visible tab writes Recent Searches.
    if (!isActive) return;
    // Strip ``kwInput`` before recording: a keyword result can settle after
    // the user has already switched to a kind scope, and a recorded URL
    // carrying ``kwInput=<kind>`` would replay this keyword search into chip
    // results instead of the keyword results it actually is (P2-2). Replay
    // lands in All, which shows keyword results.
    const recordedParams = new URLSearchParams(location.search);
    recordedParams.delete('kwInput');
    // Refine chips / grouping are view state, not part of the search: a
    // replayed Recent Search starts unfiltered, and changing a chip must not
    // record a "new" search.
    for (const k of KW_VIEW_PARAMS) recordedParams.delete(k);
    const recordedQs = recordedParams.toString();
    const url = location.pathname + (recordedQs ? `?${recordedQs}` : '');
    const key = `keyword|${url}`;
    if (lastRecordedRef.current === key) return;
    lastRecordedRef.current = key;
    const kwFilters = e.filters;
    const { groups, sources } = e.params;
    // FDA-letter keyword mode (sources === ['fda_letter']) — mirrors buildKwRequest.
    const isLetterMode = sources.length === 1 && sources[0] === 'fda_letter';
    const op = groups[0]?.inner_operator ?? 'AND';
    const label = keywordSearchTerms(groups).join(` ${op} `) || '(keyword search)';
    const count =
      data.kind === 'drugs'
        ? data.total_count ?? data.total_drugs ?? data.results?.length
        : data.results?.length;
    recordSearch({
      mode: 'keyword',
      label,
      payload: {
        url,
        groups,
        sources,
        drug_name: kwFilters.drug_name || null,
        manufacturer: kwFilters.manufacturer || null,
        regions: kwFilters.regions,
        // Application Type (marketing category) is a real, result-affecting
        // filter — record it so it both distinguishes the dedup fingerprint
        // and surfaces in the recents row (see describeSearch).
        marketing_categories: kwFilters.marketingCategories,
        // Pharmacological-class filter is sent by buildKwRequest but otherwise
        // survives only inside the url — record it so it stays part of identity.
        pharmacological_class: kwFilters.pharmacological_class,
        // FDA-letter searches carry Letter Type/Corpus/Role/Division filters
        // (buildKwRequest attaches them only in letter mode). They're real,
        // result-affecting differences that otherwise survive only inside the
        // url — record them so dropping url from the fingerprint doesn't make
        // distinct letter searches collide.
        ...(isLetterMode
          ? {
              letter_types: kwFilters.letterTypes,
              letter_corpus: kwFilters.letterCorpus,
              letter_roles: kwFilters.letterRoles,
              letter_divisions: kwFilters.letterDivisions,
            }
          : {}),
      },
      ...(count != null ? { result_count: count } : {}),
    });
  }, [recordSearch, reportRun, isActive, location]);

  // Replay a recorded search: navigate to its URL (reusing the page's
  // URL-restore machinery) and switch to the right tab.
  const replaySearch = useCallback(
    (entry: SearchHistoryEntry) => {
      const path = replayPath(entry);
      if (!path) return;
      // A fresh replay should land on page 1, not re-record stale state.
      lastRecordedRef.current = '';
      // Force the keyword form to remount even when the replayed URL matches
      // the current one (re-clicking the same entry) — otherwise its ``key``
      // wouldn't change and the form would keep the user's in-progress edits.
      setKwReplayNonce((n) => n + 1);
      navigate(path);
    },
    [navigate],
  );

  // Reopen a saved starred list in the keyword compare view. Restores the
  // originating search into sessionStorage (so the rail + highlighting rebuild)
  // then loads /compare-keyword with those drugs starred.
  const restoreSavedList = useCallback(
    (list: SavedStarredList) => {
      const path = savedListPath(list);
      if (!path) return;
      handOffSavedList(list, storageKey);
      navigate(path);
    },
    [navigate, storageKey],
  );

  // Update pending filters (UI state only, doesn't trigger search)
  const handleFiltersChange = (newFilters: SearchFilterOptions) => {
    setPendingFilters(newFilters);
  };

  // Sync local state when URL params change (e.g., browser back/forward)
  useEffect(() => {
    if (urlQuery !== searchQuery) {
      setSearchQuery(urlQuery);
    }
    if (urlPage !== currentPage) {
      setCurrentPage(urlPage || 1);
    }
  }, [urlQuery, searchQuery, urlPage, currentPage]);

  // Reverse-sync AI-search filters from URL params (browser back/forward).
  //
  // Without this, navigating to a prior search URL repaints the address
  // bar but leaves ``pendingFilters`` / ``appliedFilters`` stuck on the
  // newer in-memory state — pills, applied filters, and the rerun query
  // all diverge from what the URL says.  Sync BOTH pending (pill UI) and
  // applied (rerun source-of-truth) so they agree with the URL on every
  // back/forward.  All five filter dimensions managed by
  // ``updateUrlParams`` are kept in sync; the bailout pattern preserves
  // identity when nothing changed so React can short-circuit downstream
  // memos.
  useEffect(() => {
    const arrEq = (a: readonly string[], b: readonly string[]) =>
      a.length === b.length && a.every((v, i) => v === b[i]);
    const sync = (prev: SearchFilterOptions): SearchFilterOptions => {
      // Non-multi-review URLs intentionally omit sbaDocTypes. Preserve the
      // last in-memory pending/applied set while another corpus is active;
      // an encoded multi-review URL remains authoritative on back/forward.
      const restoredSbaDocTypes = urlSearchMode === 'multi_review'
        ? urlSbaDocTypes
        : normalizeSbaDocTypes(prev.sbaDocTypes);
      if (
        prev.searchMode === urlSearchMode
        && arrEq(prev.marketingCategories, urlCategories)
        && arrEq(prev.regions, urlRegions)
        && arrEq(prev.sbaDocTypes ?? [], restoredSbaDocTypes)
        && arrEq(prev.letterTypes, urlLetterTypes)
        && arrEq(prev.letterCorpus, urlLetterCorpus)
        && arrEq(prev.letterRoles, urlLetterRoles)
        && arrEq(prev.letterDivisions, urlLetterDivisions)
      ) return prev;
      const restored = {
        searchMode: urlSearchMode,
        marketingCategories: urlCategories,
        regions: urlRegions,
        sbaDocTypes: [...restoredSbaDocTypes],
        letterTypes: urlLetterTypes,
        letterCorpus: urlLetterCorpus,
        letterRoles: urlLetterRoles,
        letterDivisions: urlLetterDivisions,
      };
      // B3: downgrade searchMode if the URL-restored mode is not valid for
      // the cogni surface + the restored regions (e.g. multi_review restored
      // from a stale URL while regions is now ['US','EU']).
      return coerceSearchMode(restored, 'cogni', isAdmin, hasLabelNegotiations);
    };
    setPendingFilters(sync);
    setAppliedFilters(sync);
    // ``labelNegotiations`` is an additive URL param (not part of the
    // SearchFilterOptions object synced above), so reconcile it from the URL
    // here too.  Without this, browser back/forward or in-app navigation that
    // changes ``searchParams`` without remounting leaves
    // ``includeLabelNegotiations`` stuck at its mount value — a saved/history
    // URL with ``labelNegotiations=true`` would render without sending
    // ``include_label_negotiations``, and a URL without the param would keep
    // drafts enabled.  Identity-preserving updater so an unchanged value
    // doesn't trigger a re-render.
    setIncludeLabelNegotiations((prev) =>
      prev === urlLabelNegotiations ? prev : urlLabelNegotiations,
    );
    // ``kwReplayNonce``: re-run on a same-URL Recent-Search replay so AI-tab
    // page-level filters also re-restore (react-router memoizes searchParams by
    // location.search, so an identical URL wouldn't re-trigger this otherwise).
    // All writes here are identity-preserving, so re-running is a safe no-op.
    //
    // ``isAdmin``: REQUIRED, not incidental. ``useDbUser`` starts at
    // ``isAdmin=false`` and only flips after its async /auth/sync resolves, so
    // the first render coerces admin-only modes down to Labels. Without this
    // dependency the effect never re-runs once admin status arrives and that
    // downgrade is permanent — an admin opening a saved admin-only-mode link
    // silently lands on Labels. ``?searchMode=multi_review`` is the canonical
    // example: MDR is admin-only as of 2026-08-17, so an admin's saved MDR
    // link is exactly the case this dependency repairs. Do not drop the dep.
    // The
    // identity bailout above does not mask the repair: after a downgrade
    // ``prev.searchMode`` no longer matches ``urlSearchMode``, so the rebuild
    // runs and re-coerces with the now-correct admin status.
    //
    // ``hasLabelNegotiations``: REQUIRED for exactly the same reason. It also
    // starts false and flips after /auth/sync, so without it here a tenant
    // that DOES have drafts opening a saved ``?searchMode=draft_review`` link
    // would be downgraded to Labels permanently.
  }, [searchParams, kwReplayNonce, isAdmin, hasLabelNegotiations]);  // eslint-disable-line react-hooks/exhaustive-deps

  // Sync keyword search state when URL params change (back/forward navigation)
  //
  // Three-branch shape (fixup-3 after T3+T4):
  //
  //   1. ``mode !== 'keyword'``  → bail, don't interfere with other tabs.
  //   2. ``mode === 'keyword' && kwConfig`` present → restore submitted
  //      search (kwRequest / kwPage / lastKwSearchParams from the URL).
  //   3. ``mode === 'keyword' && !kwConfig`` → bare pre-search state:
  //      reset kwRequest=null, lastKwSearchParams=null, kwPage=1 so a
  //      stale ``kwRequest`` doesn't survive an app-generated back-nav
  //      to ``/?mode=keyword`` (where ``keywordSearchQuery`` would
  //      otherwise keep rendering results despite the URL saying
  //      "pre-search").
  //
  // The ``kwSearchMode`` reverse-sync (fixup-2) runs in BOTH branches 2
  // and 3 — hoisted out of the kwConfig conditional so bare back-nav
  // also keeps the visible Document Type pill in sync with the URL.
  //
  // Loop safety: ``setKwRequest(null)`` / ``setKwPage(1)`` rely on
  // React's identity bailout (Object.is) — same-value writes are no-ops.
  // ``setKwFilters`` uses the functional bailout pattern (return prev
  // when unchanged).  ``lastKwSearchParams`` is a ref and doesn't
  // trigger renders.  Any URL→state→URL cycle is broken on the second
  // pass.
  const lastKwRestoreKeyRef = useRef<string | null>(null);
  useEffect(() => {
    const mode = searchParams.get('mode');
    if (mode !== 'keyword') {
      // Clear the guard on every non-keyword visit: the skip below must
      // only ever suppress a re-restore between two CONSECUTIVE keyword-mode
      // URLs that differ solely in chips/kwInput, never a (re-)entry into
      // keyword mode from another tab — otherwise a stale key surviving a
      // Cogni detour (e.g. replay the same keyword search twice, switch to
      // Cogni, then browser Back to that same keyword URL) would early-return
      // before the restore below and leave the form blank under a Keyword URL.
      lastKwRestoreKeyRef.current = null;
      return;
    }

    // Legacy ``?kwInput=`` / ``?chips=`` links (Instant chip search, retired):
    // converted once, then the URL is rewritten without them and this effect
    // re-runs on the result. A kind scope WITH chips becomes a ``kwConfig``
    // (replacing any existing one, as the chip view used to win); a kind with
    // no chips types row 0; anything else just drops the params.
    const legacyKwInput = searchParams.get('kwInput');
    const legacyChips = searchParams.get('chips');
    if (legacyKwInput !== null || legacyChips !== null) {
      const kind = parseKwInput(legacyKwInput);
      const isKind = kind !== 'all' && kind !== 'label';
      const chips = parseChips(legacyChips);
      const params = new URLSearchParams(searchParams);
      params.delete('kwInput');
      params.delete('chips');
      if (isKind && chips.length > 0) {
        const { groups, truncated } = chipsToGroups(chips);
        // The URL's own Application Type selection, not ``initialKwFilters``
        // (which hardcodes the defaults and would drop ANDA / generics).
        const catsParam = searchParams.get('kwCats');
        const marketingCategories = catsParam !== null
          ? catsParam.split(',').filter(Boolean) : DEFAULT_MARKETING_CATEGORIES;
        const sources = selectLabelSources(initialKwRegions);
        // The chips REPLACE any old kwConfig (spec §6.4), so none of its
        // drug / manufacturer / class filters may narrow the converted search.
        const request = buildKwRequest(
          { ...initialKwFilters, drug_name: '', manufacturer: '', pharmacological_class: [], marketingCategories },
          groups, sources.length > 0 ? sources : ['label'], 0, kwVersionScope,
        );
        params.set('kwConfig', JSON.stringify(request));
        params.delete('kwPage');
        setKwMigrationNotice(truncated
          ? 'This link had more than 10 values in one field; only the first 10 are shown.' : null);
      } else if (isKind) {
        setKwRowType((prev) => ({ type: kind, nonce: prev.nonce + 1 }));
      }
      setSearchParams(params, { replace: true });
      return;
    }

    // ``searchParams`` is a NEW object every navigation, so this effect
    // re-runs on ANY URL write while on the keyword tab — including
    // ``handleChipsChange``'s ``chips=`` write and
    // ``handleKwInputModeChange``'s ``kwInput=`` write, neither of which
    // this effect's restore logic reads. Without a skip, adding a chip or
    // flipping Keyword<->Instant would re-run the restore below and
    // clobber any in-progress (unsubmitted) Document Type / region / drug
    // filter edit with whatever the last-SUBMITTED ``kwConfig`` says (P2-1).
    // Build a key from every OTHER param (plus the two extra deps below)
    // and bail when it hasn't actually changed.
    const restoreKey = (() => {
      const params = new URLSearchParams(searchParams);
      params.delete('chips');
      params.delete('kwInput');
      // Results-table view state (Refine chips / grouping) — display only.
      for (const k of KW_VIEW_PARAMS) params.delete(k);
      return `${params.toString()}|${kwReplayNonce}|${isAdmin}|${isAdminSettled}`;
    })();
    if (lastKwRestoreKeyRef.current === restoreKey) return;
    lastKwRestoreKeyRef.current = restoreKey;

    const kwConfig = searchParams.get('kwConfig');
    const kwPageParam = searchParams.get('kwPage');
    // Admin "Search in" follows the restored request (PD11).
    setKwDocSources((prev) => {
      const next = deriveKwDocSources(kwConfig);
      return prev.join(',') === next.join(',') ? prev : next;
    });

    if (kwConfig) {
      try {
        const page = kwPageParam ? Number(kwPageParam) : 1;
        const parsed = withKwPage(JSON.parse(kwConfig) as KeywordSearchRequest, page);
        // PD10: sanitized synchronously for a settled non-admin (the coercion
        // effect rewrites the URL right after).
        const request = !adminLoading && !isAdminSettled && kwRequestNeedsAdmin(parsed)
          ? sanitizeKwRequestForNonAdmin(parsed) : parsed;
        kwSurfaceRef.current?.setRequest(request);
        kwSurfaceRef.current?.setPage(page);
        kwSurfaceRef.current?.setSort(readKwSort(request));
        const restoredSources: KeywordSearchSource[] =
          request.sources && request.sources.length > 0 ? request.sources : ['label'];
        if (request.groups && request.groups.length > 0) {
          kwSurfaceRef.current?.setLastSearchParams(
            { groups: request.groups, sources: restoredSources });
        } else if (request.rows && request.rows.length > 0) {
          // Legacy URL: convert rows to single group
          kwSurfaceRef.current?.setLastSearchParams({
            groups: [{ rows: request.rows, inner_operator: request.row_operator ?? 'AND', proximity: request.proximity ?? null }],
            sources: restoredSources,
          });
        }
      } catch {
        // Invalid config in URL, ignore
      }
    } else {
      // Bare /?mode=keyword — reset to pre-search state (matches the
      // fresh-load init: kwRequest=null, kwPage=1, no cached params).
      kwSurfaceRef.current?.setRequest(null);
      kwSurfaceRef.current?.setLastSearchParams(null);
      kwSurfaceRef.current?.setPage(1);
      kwSurfaceRef.current?.setSort(null);
    }

    // Reverse-sync the Document Type pill from ``?kwSearchMode=``.
    // Runs in BOTH the kwConfig-present and bare branches so bare
    // back-nav doesn't leave the pill stale.  Absent / invalid →
    // fall back to the default ('labels').  ``VALID_KW_SEARCH_MODES``
    // is module-scope.
    const urlKwSearchModeValue = searchParams.get('kwSearchMode');
    const validatedKwSearchMode: SearchModeValue =
      urlKwSearchModeValue && VALID_KW_SEARCH_MODES.has(urlKwSearchModeValue)
        ? (urlKwSearchModeValue as SearchModeValue)
        : DEFAULT_SEARCH_MODE;
    // Reverse-sync the letter-filter pill rows from the saved
    // ``kwConfig`` request body. Without this, back-nav rehydrates the
    // submitted request (results show filtered output) while the visible
    // pills stay at defaults — pill state and results diverge.
    let restoredLetterTypes: string[] = DEFAULT_LETTER_TYPES;
    let restoredLetterCorpus: string[] = DEFAULT_LETTER_CORPUS;
    let restoredLetterRoles: string[] = DEFAULT_LETTER_ROLES;
    let restoredLetterDivisions: string[] = DEFAULT_LETTER_DIVISIONS;
    // Restore the REGION pills from the saved request too. Back-nav from
    // Compare (and reload / shared link) otherwise left the pills at the US
    // default while the results were EU/CA, because this effect reasserted
    // every other filter EXCEPT regions — so a stale US won the race over the
    // mount-time initialKwRegions. ``regions`` is preferred over the legacy
    // singular ``region``; absent kwConfig → DEFAULT_REGIONS (US).
    let restoredRegions: SearchRegion[] = DEFAULT_REGIONS;
    // Reverse-sync the Drug / Manufacturer / Pharmacological-class inputs from
    // the saved request too — the effect otherwise reasserted every other
    // filter EXCEPT these three, so on any ``searchParams`` change (e.g. after
    // submit rewrites the URL) they were clobbered back to empty while the
    // executed request kept the real values. Absent kwConfig → empty.
    let restoredDrugName = '';
    let restoredManufacturer = '';
    let restoredPharmClass: string[] = [];
    // Legacy "Other Documents" searches dropped region from the saved request by
    // design, so the restore above would otherwise reset the (hidden) region
    // pills to the US default. Detect that from the saved sources and preserve
    // the prior region state instead of clobbering it to US — a neutral
    // all-region intent.
    //
    // Retained after the 2026-07-29 removal of that mode: no NEW search can
    // carry these sources, but pre-removal rows still exist in search_history,
    // and replaying one should still render coherently.
    const HA_REVIEW_SOURCES = new Set(['sba', 'epar', 'fda_letter', 'uk_pil', 'au_cmi', 'jp_pdf']);
    let savedIsOtherDocs = false;
    // Marketing-category (Application Type) pills live in their own ``kwCats``
    // URL param (frontend values, lossless). Absent → defaults (NDA+BLA).
    const kwCatsParam = searchParams.get('kwCats');
    const restoredCategories: string[] =
      kwCatsParam !== null ? kwCatsParam.split(',').filter(Boolean) : DEFAULT_MARKETING_CATEGORIES;
    if (kwConfig) {
      try {
        const saved = JSON.parse(kwConfig) as KeywordSearchRequest & { regions?: string[] };
        if (Array.isArray(saved.letter_types)) restoredLetterTypes = saved.letter_types;
        if (Array.isArray(saved.letter_corpus)) restoredLetterCorpus = saved.letter_corpus;
        if (Array.isArray(saved.letter_roles)) restoredLetterRoles = saved.letter_roles;
        if (Array.isArray(saved.letter_divisions)) restoredLetterDivisions = saved.letter_divisions;
        if (Array.isArray(saved.regions) && saved.regions.length) {
          restoredRegions = saved.regions as SearchRegion[];
        } else if (saved.region && (saved.region as string) !== 'ALL') {
          restoredRegions = [saved.region as SearchRegion];
        }
        if (typeof saved.drug_name === 'string') restoredDrugName = saved.drug_name;
        if (typeof saved.manufacturer === 'string') restoredManufacturer = saved.manufacturer;
        if (Array.isArray(saved.pharmacological_class)) restoredPharmClass = saved.pharmacological_class;
        if (Array.isArray(saved.sources)) {
          savedIsOtherDocs = saved.sources.some((s) => HA_REVIEW_SOURCES.has(s));
        }
      } catch {
        // Already logged at the earlier parse — pill arrays stay at defaults.
      }
    }
    kwSurfaceRef.current?.setFilters((prev) => {
      // Other Documents drops region by design → keep the prior region state
      // rather than resetting the (hidden) pills to US.
      const effectiveRegions = savedIsOtherDocs ? prev.regions : restoredRegions;
      const sameMode = prev.searchMode === validatedKwSearchMode;
      const sameTypes =
        prev.letterTypes.length === restoredLetterTypes.length
        && prev.letterTypes.every((v, i) => v === restoredLetterTypes[i]);
      const sameCorpus =
        prev.letterCorpus.length === restoredLetterCorpus.length
        && prev.letterCorpus.every((v, i) => v === restoredLetterCorpus[i]);
      const sameRoles =
        prev.letterRoles.length === restoredLetterRoles.length
        && prev.letterRoles.every((v, i) => v === restoredLetterRoles[i]);
      const sameDivisions =
        prev.letterDivisions.length === restoredLetterDivisions.length
        && prev.letterDivisions.every((v, i) => v === restoredLetterDivisions[i]);
      const sameRegions =
        prev.regions.length === effectiveRegions.length
        && prev.regions.every((v, i) => v === effectiveRegions[i]);
      const sameCategories =
        prev.marketingCategories.length === restoredCategories.length
        && prev.marketingCategories.every((v, i) => v === restoredCategories[i]);
      const sameDrugName = prev.drug_name === restoredDrugName;
      const sameManufacturer = prev.manufacturer === restoredManufacturer;
      const samePharmClass =
        prev.pharmacological_class.length === restoredPharmClass.length
        && prev.pharmacological_class.every((v, i) => v === restoredPharmClass[i]);
      if (
        sameMode && sameTypes && sameCorpus && sameRoles && sameDivisions
        && sameRegions && sameCategories
        && sameDrugName && sameManufacturer && samePharmClass
      ) return prev;
      const restored = {
        ...prev,
        regions: effectiveRegions,
        marketingCategories: restoredCategories,
        searchMode: validatedKwSearchMode,
        letterTypes: restoredLetterTypes,
        letterCorpus: restoredLetterCorpus,
        letterRoles: restoredLetterRoles,
        letterDivisions: restoredLetterDivisions,
        drug_name: restoredDrugName,
        manufacturer: restoredManufacturer,
        pharmacological_class: restoredPharmClass,
      };
      // B3: downgrade stale multi_review/draft_review from URL — keyword
      // surface only serves labels + fda_letters.
      // ``hasLabelNegotiations`` is deliberately NOT threaded here: neither
      // Label Negotiations mode is in SURFACE_DOC_TYPES.keyword, so both are
      // already downgraded by the surfaceModes check regardless of the flag.
      // Passing it would only add a needless effect re-run when /auth/sync
      // resolves.
      // coerceSearchMode returns SearchFilterOptions (base type); cast back to
      // KeywordFilters because `restored` carries drug_name / manufacturer /
      // pharmacological_class (restored explicitly above, and via the `...prev`
      // spread) and coerceSearchMode only ever spreads its input, never drops
      // fields.
      return coerceSearchMode(restored as SearchFilterOptions, 'keyword', isAdmin) as KeywordFilters;
    });
    // ``kwReplayNonce`` is in the deps so a Recent-Search replay to the SAME URL
    // still re-restores page-level filters (regions / Application Type / letter
    // pills / drug / manufacturer / class). react-router memoizes ``searchParams``
    // by ``location.search``, so an identical replayed URL wouldn't otherwise
    // re-run this effect and stale filters the user changed after that search
    // would survive. The restore is bailout-guarded, so re-running is a no-op
    // when nothing actually changed.
    //
    // ``isAdmin`` for the same reason as the Cogni sync effect above: it
    // arrives asynchronously, and without re-running, a first-render
    // coercion made while it was still ``false`` would stick permanently.
    // ``isAdminSettled`` (SI5): the admin "Search in" restore above depends
    // on the settled flag too.
  }, [searchParams, kwReplayNonce, isAdmin, isAdminSettled]);  // eslint-disable-line react-hooks/exhaustive-deps

  // All results displayed at once (up to 50, LLM-reranked)
  const totalResults = searchResults?.results?.length || 0;
  const currentResults = searchResults?.results || [];

  const handleDrugClick = (drugId: number, result?: DrugResultV2, sectionOverride?: string | null) => {
    // Open the document the best evidence came from (a drug can carry several
    // SmPCs, e.g. one per strength); otherwise the drug's default label.
    const docHref = cogniEvidenceHref(drugId, result?.evidence);
    const params = new URLSearchParams(docHref ? docHref.slice(docHref.indexOf('?') + 1) : '');

    // Pass section number if available (for auto-scroll to matched section)
    // V2 uses evidence.section_number instead of preview_section_number
    // A cite click passes its own section (null = open the label without one).
    const section = sectionOverride !== undefined ? sectionOverride : result?.evidence?.section_number;
    if (section) {
      params.set('section', section);
    }

    // Pass the search query only for highlighting matched terms on the label.
    // The Cogni / Label AI chat is intentionally NOT seeded with the search
    // term — it opens blank.
    if (searchQuery) {
      params.set('highlight', searchQuery);
    }

    // Pass filter settings for sidebar search cache hit
    if (appliedFilters.marketingCategories.length > 0) {
      params.set('categories', appliedFilters.marketingCategories.join(','));
    }

    // Pass all evidence section numbers for multi-section highlighting on DrugDetailPage
    if (result?.all_evidence_sections && result.all_evidence_sections.length > 0) {
      params.set('sections', result.all_evidence_sections.join(','));
    }

    // Hand off the matched evidence chunk text out-of-band for chunk-level
    // highlighting (avoids URL length limits + keeps sensitive text out of
    // logs). Uses localStorage, NOT sessionStorage, so it survives into the
    // new shell tab (each tab's sessionStorage keys are private to it).
    if (result?.evidence?.chunk_text) {
      stashEvidenceChunk(drugId, result.evidence.chunk_text);
    }

    const queryString = params.toString();
    // Open the result in a NEW shell tab so the user keeps their search +
    // chat context in the current tab (mirrors the citation click-through).
    openInNewTab(`/drug/${drugId}${queryString ? `?${queryString}` : ''}`);
  };

  // Summary chip click (D2): a cite whose drug is on the current page reuses
  // the row deep link with the cite's own section; otherwise open the label.
  const handleCiteClick = (c: CogniSummaryCite) => {
    const row = currentResults.find((r) => r.drug_id === c.drug_id);
    if (row) {
      handleDrugClick(c.drug_id, row, c.section_number);
      return;
    }
    const params = new URLSearchParams();
    if (c.section_number) params.set('section', c.section_number);
    if (searchQuery) params.set('highlight', searchQuery);
    const qs = params.toString();
    openInNewTab(`/drug/${c.drug_id}${qs ? `?${qs}` : ''}`);
  };

  const showSummaryPanel =
    !appliedIncludeComments && !appliedIncludeSba && !isLetterMode &&
    appliedFilters.searchMode === 'labels' && currentResults.length > 0 &&
    !isSearchLoading && !!cogniCacheKey;

  // Open the unified compare page in Cogni mode. Mirrors the keyword flow:
  // stash the request that reproduces the AI search (query + the exact
  // hand off the EXACT results the user is looking at (not a refetch — page 1
  // is streamed, so there's no query cache to replay) in
  // sessionStorage('cogni-results'), then navigate WITHOUT ?ids. The compare
  // page maps them into its rail; the compare set is the ★ shortlist, identical
  // to keyword mode.
  const handleOpenComparison = useCallback(() => {
    if (!currentResults.length) return;
    // Hand off page 1 + the search options + total so the compare rail can
    // infinite-scroll pages 2+ via useSearchQueryV2Drugs (same call as page 2+
    // here), letting the user shortlist from ALL results, not just page 1.
    const filters = {
      top_k: 50,
      marketingCategories: appliedFilters.marketingCategories,
      regions: appliedFilters.regions,
      page_size: 50,
      searchMode: appliedFilters.searchMode,
    };
    const total = v2Results?.metadata?.total_drugs ?? currentResults.length;
    try {
      sessionStorage.setItem(
        storageKey('cogni-results'),
        JSON.stringify({ query: searchQuery, drugs: currentResults, filters, total }),
      );
    } catch {
      /* sessionStorage may be unavailable; compare page falls back to empty rail */
    }
    // Carry the results table's filters (Region / year / last-updated / refine,
    // already mirrored into this URL by SearchResultsTable) so Compare lists
    // the same drugs the user filtered the table to (2.4).
    navigate(buildCogniCompareUrl(searchParams));
  }, [currentResults, searchQuery, appliedFilters.marketingCategories, appliedFilters.regions, appliedFilters.searchMode, v2Results, navigate, storageKey, searchParams]);

  // Register the settled Cogni result set with the shell (D11).
  //
  // ALL SIX corpora, not just labels. This reported only labels at first, so
  // switching Document Type to FDA Letters or Competitive Intel ran a real
  // search, rendered real rows, and left "Use in CEPI" dark with nothing
  // saying why — the surface worked and the handoff silently did not.
  //
  // The RAW response is handed over in every case, because the rows key is
  // what the capture path reads. CI and SBA hooks expose an array rather than
  // an envelope, so those two are wrapped in the shape the backend expects
  // (`documents`) rather than the backend learning a third shape.
  //
  // Draft review is deliberately absent: it is tenant draft content, not a
  // corpus of record, and attaching it would put unreleased labelling into a
  // conversation scope that outlives the search.
  useEffect(() => {
    // Only a Cogni tab registers a Cogni run: a keyword URL can carry `q=`,
    // which starts the Cogni stream underneath. Page 2+ is the same run.
    if (searchMode !== 'ai' || currentPage !== 1) return;
    // Ranked discovery cannot prove absence, so a zero-row set carries no claim
    // and is not registered (Keyword is the opposite: its zero is a finding).
    const report = (corpus: CogniCorpus, response: unknown) => {
      const count = countRows('cogni', response, corpus);
      if (count <= 0) return;
      // Paging 1 -> 2 -> 1 refires with an equal result: same run, same seq.
      const sig = JSON.stringify([corpus, searchQuery, response]);
      if (lastCogniSigRef.current === sig) return;
      lastCogniSigRef.current = sig;
      reportRun({
        kind: 'set', capability: 'cogni', corpus, count,
        queryText: searchQuery,
        request: { query: searchQuery, corpus },
        response,
      });
    };
    const mode = appliedFilters.searchMode;
    if (mode === 'labels') {
      // `isSearchLoading` matters as much as `v2Results`. The streaming path
      // fills `v2Results` category by category, so without this a search still
      // showing "Ranking candidates…" was handed over as a finished result —
      // and the workspace offered [Use in CEPI] on 0 ranked rows (prod,
      // 2026-09-21). KeywordSurface has always guarded its own report effect
      // the same way (`query.isFetching`); this branch simply never did.
      if (isSearchLoading || !v2Results) return;
      report('labels', v2Results);
    } else if (mode === 'fda_letters') {
      if (!letterSearchQuery.data) return;
      report('fda_letters', letterSearchQuery.data);
    } else if (mode === 'approval_letters') {
      if (!approvalLetterResults.length) return;
      report('approval_letters', { results: approvalLetterResults });
    } else if (mode === 'ci') {
      if (!ciDocuments.length) return;
      report('ci', { documents: ciDocuments });
    } else if (mode === 'multi_review') {
      // HA reviews are nested under drugs now; flatten to the legacy `sba` row shape (D4).
      if (isSearchLoading || !v2Results) return;
      const documents = haDocumentsToSbaRows(v2Results.drug_results);
      if (!documents.length) return;
      report('sba', { documents });
    }
  }, [
    reportRun, searchMode, currentPage, searchQuery, appliedFilters.searchMode, v2Results, isSearchLoading,
    letterSearchQuery.data, approvalLetterResults, ciDocuments,
  ]);

  const handleCommentClick = (comment: CommentSearchResult) => {
    if (comment.drug_id && comment.label_version_id) {
      // For threaded comments (replies), scroll to the parent comment instead
      const targetCommentId = comment.parent_comment_id || comment.comment_id;
      navigate(`/drug/${comment.drug_id}?version=${comment.label_version_id}&comment=${targetCommentId}`);
    } else if (comment.drug_id) {
      navigate(`/drug/${comment.drug_id}`);
    }
  };

  // Handle click-through on a draft result hit — opens the ONLYOFFICE viewer
  // for the draft label at the matching comment (if kind='comment') or at the
  // draft root (if kind='content').
  const handleDraftHitClick = useCallback((result: DraftResultV2, hit: DraftResultV2['hits'][number]) => {
    const { path, query } = resolveDraftTarget(result, hit);
    const qs = new URLSearchParams(
      Object.fromEntries(Object.entries(query).map(([k, v]) => [k, String(v)])),
    ).toString();
    openInNewTab(`${path}${qs ? `?${qs}` : ''}`);
  }, []);

  // Height chain: workspace column (flex-col) > flex-1 div > TabPane h-full
  // overflow-auto > TabBody h-full > this root. The Cogni results layout needs a
  // DEFINITE height (h-full) so its inner overflow-y-auto engages and the top
  // bar stays put; every other surface just grows (min-h-full).
  return (
    <div className={`bg-gradient-to-br from-slate-50 via-white to-blue-50 ${
      searchMode === 'ai' && hasSearched ? 'h-full' : 'min-h-full'
    }`}>
      {/* ========== KEYWORD SEARCH TAB ========== */}
      {/* Mounted only in keyword mode. The shell keeps the TAB alive across tab
        * switches, so the surface's state survives glancing at another tab;
        * within one tab the mode is fixed by ``?mode=``. ``hidden`` keeps a
        * background tab's surface quiet (no Recent-Searches entry, no run
        * report for a tab nobody is looking at).
        */}
      {searchMode === 'keyword' && (
      <div className="max-w-6xl mx-auto px-4 py-6">
        <KeywordSurface
          ref={kwSurfaceRef}
          usFdaVisible={isUsFdaVisible(isAdmin)}
          initialFilters={initialKwFilters}
          initialRequest={initialKw.request}
          initialPage={initialKw.page}
          initialSort={initialKw.sort}
          initialLastSearchParams={initialKw.params}
          /* Remount the FORM when the URL's kwConfig changes (browser
             back/forward, shared link) or a Recent Search is replayed — the
             nonce is needed because re-clicking the SAME entry produces an
             identical URL, so kwConfig alone wouldn't change. The corpus part
             of the key is added inside the surface, which owns searchMode. */
          formKey={`${searchParams.get('kwConfig') || 'fresh'}:${kwReplayNonce}:${kwRowType.nonce}`}
          initialRowType={kwRowType.type}
          /* Derived synchronously from the URL during render, NOT from a ref
             written in a post-render effect — the form reads initialGroups /
             initialSources only at mount, so they must already hold the
             restored values at remount time. */
          restoredSearchParams={restoredKwSearchParams}
          versionScope={kwVersionScope}
          /* ADMIN ONLY (spec 2026-10-16 §6a): gated on the SETTLED flag, like
             the Original-label toggle; non-admins get today's surface. */
          {...(isAdminSettled ? {
            docSources: kwDocSources, onDocSourcesChange: setKwDocSources,
          } : {})}
          hidden={!isActive}
          onSearchExecuted={handleKwSearchExecuted}
          onResults={handleKwResults}
          header={({ corpus }) => (
            <>
              <div className="flex flex-wrap items-center justify-between gap-3">
                <h1 className="font-display text-[24px] font-medium tracking-[-0.015em] text-ink">
                  Keyword search
                </h1>
                <div className="flex flex-wrap items-center gap-2">
                {/* Origin version scope — ADMIN ONLY.
                  *
                  * Gated on ``isAdminSettled`` (useDbUser), never ``isAdmin``
                  * (useIsAdmin): the latter reports false while /auth/sync is
                  * in flight, which would hide the control from a real admin
                  * and make it flicker in. Hiding it is a convenience anyway —
                  * the backend re-checks platform-admin and 403s regardless.
                  *
                  * Labels only: the origin corpus is built exclusively from
                  * region='US' labels, so it has nothing to say about FDA
                  * letters. */}
                {isAdminSettled && corpus === 'labels' && (
                  <label
                    className={`flex h-[34px] cursor-pointer select-none items-center gap-2 rounded-ctl
                      border px-3 text-[13px] transition-colors ${
                      isOriginScope
                        ? 'border-warn-line bg-warn-bg text-warn-ink'
                        : 'border-line-input bg-white text-ink hover:border-ink-3'
                    }`}
                    title="Search each product's ORIGINAL label instead of its current one (admin only)"
                  >
                    <input
                      type="checkbox"
                      className="accent-accent"
                      checked={isOriginScope}
                      onChange={(e) => {
                        const on = e.target.checked;
                        setIsOriginScope(on);
                        // Keep the URL in step so the state survives reload and
                        // can be shared — and so the admin coercion effect has
                        // something to strip for a non-admin.
                        setSearchParams((prev) => {
                          const next = new URLSearchParams(prev);
                          if (on) next.set('is_origin', 'true');
                          else next.delete('is_origin');
                          return next;
                        }, { replace: true });
                      }}
                    />
                    Original label
                  </label>
                )}
                <RecentSearches onReplay={replaySearch} />
                <SavedListsPanel onRestore={restoreSavedList} />
                </div>
              </div>

              {/* Persistent banner while origin scope is active. Without it the
                * only cue is a small checkbox, and every result on screen is
                * from a DIFFERENT label than the one users normally see — a
                * mode you can forget you are in is a mode that produces misread
                * results. */}
              {isOriginScope && (
                <div className="rounded-md border border-warn-line bg-warn-bg px-4 py-2.5
                                text-sm text-warn-ink">
                  <span className="font-semibold">Searching ORIGINAL labels.</span>{' '}
                  Results come from each product's earliest held version, not its
                  current label — median 3 versions / 3.7 years older. Some are the
                  oldest version we hold rather than the true original.
                </div>
              )}
              {kwMigrationNotice && (
                <div role="status" className="rounded-md border border-warn-line bg-warn-bg px-4 py-2.5 text-sm text-warn-ink">
                  {kwMigrationNotice}
                </div>
              )}
            </>
          )}
        />
      </div>
      )}
      {/* ========== LABEL RESEARCHER TAB ========== */}
      {searchMode === 'researcher' && (
        // Per-surface corpus derivation (Task 3): ResearchPanel binds the
        // Document Type pill to its own local ``filters.searchMode`` and
        // derives the corpus for the POST body internally.  SearchPage
        // passes no corpus prop.
        <ResearchPanel />
      )}

      {/* ========== WORKSTREAMS TAB (CEPI Agents) ========== */}
      {searchMode === 'workstream' && (
        <WorkstreamCreatePanel
          onCreated={(id) => navigate(`/workstream/${id}`)}
        />
      )}

      {/* ========== AI SEARCH TAB ========== */}
      {searchMode === 'ai' && !hasSearched ? (
        /* ========== HERO LAYOUT (before first search) ========== */
        <>
          {/* Hero Section — background intentionally left as the plain page
              gradient (from-slate-50 via-white to-blue-50) so Cogni matches
              the Investigator surface. The earlier indigo/purple/pink tint +
              blurred colour blobs were removed for that parity. */}
          <div className="relative overflow-hidden">
            {/* ``max-w-6xl`` (not ``container``) so Cogni's content width
                matches the Keyword and Investigator surfaces. */}
            <div className="relative max-w-6xl mx-auto px-4 pt-[15px] pb-12">
              {/* Search Bar.  Task 3 removed the T4 "coming soon" notice
                  for FDA Letters — Cogni now dispatches against the letter
                  corpus via ``useSearchQueryV2Letters``, so the SearchBar
                  is the entry point for all four Document Type values.
                  The SearchFilters row below remains the Document Type
                  switcher. */}
              <div className="max-w-6xl mx-auto">
                {/* Compact brand mark — left-aligned to the search box (the
                    inner content box) so it matches the Investigator surface. */}
                <div className="mb-[15px] pl-2">
                  <BrandMark size="sm" />
                </div>
                <div className="relative">
                  <div className="absolute -inset-1 bg-gradient-to-r from-indigo-500 via-purple-500 to-pink-500 rounded-2xl blur opacity-20" />
                  <div className="relative bg-white rounded-xl shadow-xl shadow-slate-200/50 p-6">
                    {/* Surface title + tagline — mirrors the Investigator header
                        (h2 + subtext) for cross-tab consistency. */}
                    <h2 className="text-lg font-semibold text-slate-800 mb-1">
                      Cogni (NL Search)
                    </h2>
                    <p className="text-sm text-slate-500 mb-4">
                      Use natural language to search by meaning, not just keywords.
                    </p>
                    <SearchBar
                      onSearch={handleHybridSearch}
                      isLoading={isLoading}
                      initialQuery={searchQuery}
                    />
                  </div>
                </div>

                {/* Recent searches is a keyword-only feature — not shown on the
                    Q&A (Cogni) home. The keyword view renders it in its top bar. */}

                {/* Search Filters - all toggles in one row */}
                <div className="mt-6">
                  <SearchFilters
                    corpus={aiPendingCorpus}
                    filters={pendingFilters}
                    onFiltersChange={handleFiltersChange}
                    isLoading={isLoading}
                    surface="cogni"
                    includeLabelNegotiations={includeLabelNegotiations}
                    {...(isAuthenticated ? { onIncludeLabelNegotiationsChange: handleToggleLabelNegotiations } : {})}
                  />
                </div>
              </div>
            </div>
          </div>
        </>
      ) : searchMode === 'ai' ? (
        /* ========== RESULTS LAYOUT (after search) ========== */
        <div className="flex flex-col h-full">
          {/* Question box + applied-scope chips + collapsible filters */}
          <div className="flex-shrink-0 border-b border-line bg-white px-3 py-3">
            <form
              onSubmit={(e) => { e.preventDefault(); if (topBarQuery.trim()) handleHybridSearch(topBarQuery); }}
              className="flex items-center gap-3 min-h-[56px] border border-line-input rounded px-3 py-2 bg-white"
            >
              <span className="bg-ink text-ground font-mono text-[11px] tracking-[0.12em] px-2 py-1 rounded-sm shrink-0">COGNI</span>
              <textarea
                ref={topBarTextareaRef}
                aria-label="Question"
                value={topBarQuery}
                onChange={(e) => {
                  setTopBarQuery(e.target.value);
                  // Auto-resize
                  const el = e.target;
                  el.style.height = 'auto';
                  el.style.height = `${el.scrollHeight}px`;
                }}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' && !e.shiftKey) {
                    e.preventDefault();
                    if (topBarQuery.trim()) handleHybridSearch(topBarQuery);
                  }
                }}
                placeholder="Ask a question about drug labels..."
                rows={1}
                className="flex-1 text-base text-ink bg-transparent focus:outline-none resize-none overflow-hidden"
              />
              <button type="submit" className="btn-primary shrink-0">Ask</button>
            </form>
            <div className="mt-2">
              <CogniScopeChips
                filters={appliedFilters}
                draftsIncluded={isAuthenticated && appliedFilters.searchMode === 'labels' ? includeLabelNegotiations : null}
                filtersOpen={filtersOpen}
                onToggleFilters={() => setFiltersOpen((o) => !o)}
                controlsId="cogni-edit-filters"
                usFdaVisible={isUsFdaVisible(isAdmin)}
              />
            </div>
            <div id="cogni-edit-filters" hidden={!filtersOpen} className="mt-2">
              {filtersOpen && (
                <SearchFilters
                  corpus={aiPendingCorpus}
                  filters={pendingFilters}
                  onFiltersChange={handleFiltersChange}
                  isLoading={isLoading}
                  compact
                  surface="cogni"
                  includeLabelNegotiations={includeLabelNegotiations}
                  {...(isAuthenticated ? { onIncludeLabelNegotiationsChange: handleToggleLabelNegotiations } : {})}
                />
              )}
            </div>
          </div>

          {/* Main content: Results (left) + CEPI Chat (right).  Task 3
              removed the T4 "coming soon" notice for FDA Letters — letter
              search now dispatches via ``letterSearchQuery`` and the
              letter results render inside the same results column below.
              The chat panel right rail stays drug-only in v1 (the
              ``hasDrugScope`` gate at the bottom of this branch naturally
              suppresses it when ``currentResults`` is empty in letter
              mode); chat reintegration is the deferred follow-up. */}
          <div className="flex flex-1 overflow-hidden">
            {/* Results Pane */}
            <div className="flex-1 overflow-y-auto">
              <div className="px-3 pb-16">
        {/* Error Display */}
        {error && (
          <div className="mt-3 p-4 bg-red-50 border border-red-100 rounded-xl text-red-700 shadow-sm">
            <div className="flex items-start gap-3">
              <span className="text-red-400 mt-0.5">⚠</span>
              <div>
                <p className="font-medium">Search Error</p>
                <p className="text-sm mt-1 text-red-600">{error}</p>
              </div>
            </div>
          </div>
        )}

        {/* Streaming: full-page loading indicator when no results yet */}
        {useStreaming && isStreamingLoading && streamingDrugCount === 0 && !searchResults && (
          <div className="mt-6">
            <div className="flex flex-col items-center justify-center py-20">
              <div className="relative mb-6">
                <div className="w-16 h-16 border-4 border-indigo-100 rounded-full"></div>
                <div className="absolute top-0 left-0 w-16 h-16 border-4 border-indigo-500 rounded-full border-t-transparent animate-spin"></div>
              </div>
              <p className="text-lg font-medium text-slate-700">Analyzing drug labels...</p>
              <p className="text-sm text-slate-400 mt-2">Pre-filtering and ranking candidates</p>
            </div>
          </div>
        )}

        {/* Results Section */}
        {(searchResults || appliedIncludeComments || appliedIncludeSba || appliedIncludeCi || appliedIncludeApprovalLetters || hasLetterActivity) && (
          <div className="mt-3">
            {/* Interpreted-as line - only in normal search mode */}
            {!appliedIncludeComments && !isLetterMode && searchResults?.intent && (
              <p className="mb-4 text-[13px] text-ink-2">
                <strong className="text-ink">Interpreted as</strong> {searchResults.intent}{' '}
                <button
                  type="button"
                  onClick={() => { topBarTextareaRef.current?.focus(); topBarTextareaRef.current?.select(); }}
                  className="text-accent hover:underline"
                >
                  Refine
                </button>
              </p>
            )}

            {/* On-request AI summary (labels mode, finished search, cache_key present) */}
            {showSummaryPanel && (
              <div className="mb-6">
                <CogniSummaryPanel
                  state={summary.state}
                  onGenerate={summary.generate}
                  onHide={summary.hide}
                  onCiteClick={handleCiteClick}
                  onContinueInInvestigator={() => openInNewTab('/?mode=researcher&rq=' + encodeURIComponent(searchQuery))}
                />
              </div>
            )}

            {/* Results Header with Tabs */}
            <div className="mb-6">
              <div className="flex items-end justify-between">
                <div>
                  {!appliedIncludeComments && isCogniDrugMode ? (
                    <h2 className="font-display text-[22px] font-medium tracking-tight text-ink">
                      {v2Results?.metadata?.total_drugs || totalResults} {appliedIncludeSba ? 'drugs with HA reviews' : 'labels'}
                    </h2>
                  ) : (
                    <h2 className="text-2xl font-semibold text-slate-800">
                      {appliedIncludeComments ? 'Label Negotiations' : 'Results'}
                    </h2>
                  )}
                </div>
                {/* Compare + Share — normal drug search mode only. Compare opens
                    the unified /compare-keyword page in Cogni mode: the AI
                    results become the rail (★ to shortlist, drag into panels). */}
                {isNormalSearchMode && !appliedIncludeComments && activeResultTab === 'drugs' && currentResults.length > 0 && v2Results && (
                  <div className="flex items-center gap-2">
                    <ShareButton
                      query={searchQuery}
                      drugResults={currentResults}
                      filters={appliedFilters as unknown as Record<string, unknown>}
                      intent={v2Results.intent}
                      evidenceChunks={v2Results.evidence_chunks}
                      {...(summary.state.phase === 'ready' ? { summary: summaryShareHtml(summary.state.summary) } : {})}
                      metadata={v2Results.metadata}
                      resultCount={totalResults}
                    />
                    <button
                      onClick={handleOpenComparison}
                      disabled={currentResults.length === 0}
                      title="Compare these results side-by-side (★ to shortlist, drag into panels)"
                      className="btn-primary"
                    >
                      Compare
                    </button>
                  </div>
                )}

              </div>

              {/* Class-only enumeration / unrecognized-class notice (additive — RAG mode renders nothing here) */}
              {v2Results?.metadata?.notice && (
                <div className="mt-2 rounded-md bg-amber-50 border border-amber-200 px-3 py-2 text-sm text-amber-800">
                  {v2Results.metadata.notice}
                </div>
              )}
              {v2Results?.metadata?.result_mode === 'enumeration' && (
                <p className="mt-2 text-sm text-slate-500">
                  Showing {(v2Results.metadata.total_drugs ?? 0).toLocaleString()} drugs in this class (browse view).
                </p>
              )}

              {/* Result Type Tabs - only show in normal search mode if we have comment results */}
              {!appliedIncludeComments && !appliedIncludeSba && searchResults?.comment_results && searchResults.comment_results.length > 0 && (
                <div className="mt-4 flex gap-2 border-b border-slate-200">
                  <button
                    onClick={() => setActiveResultTab('drugs')}
                    className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors ${
                      activeResultTab === 'drugs'
                        ? 'border-indigo-600 text-indigo-600'
                        : 'border-transparent text-slate-500 hover:text-slate-700'
                    }`}
                  >
                    Drug Labels ({totalResults})
                  </button>
                  <button
                    onClick={() => setActiveResultTab('comments')}
                    className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors ${
                      activeResultTab === 'comments'
                        ? 'border-indigo-600 text-indigo-600'
                        : 'border-transparent text-slate-500 hover:text-slate-700'
                    }`}
                  >
                    Draft Comments ({searchResults.total_comment_matches || 0})
                  </button>
                </div>
              )}

              {/* Count display for normal search mode (drug labels corpus only).
                  Gated on ``searchMode === 'labels'`` so it doesn't leak into
                  FDA-letters mode, where the count would otherwise read 0 from
                  the drug-side state (``v2Results`` / ``totalResults``) and
                  display "0 results" alongside the actual letter rows. */}
              {!appliedIncludeComments && activeResultTab === 'drugs' && isCogniDrugMode && (
                <p className="text-ink-2 text-sm mt-2">
                  {(() => {
                    const parts: React.ReactNode[] = [];
                    const progress = (() => {
                    // Show streaming progress when actively streaming
                    if (useStreaming && isStreamingLoading && streamingDrugCount > 0) {
                      return (<span>Found {streamingDrugCount} drugs so far<span className="animate-ellipsis"></span></span>);
                    }
                    // Show fetching indicator when streaming but no drugs yet
                    if (useStreaming && isStreamingLoading && streamingDrugCount === 0) {
                      return (
                        <span className="inline-flex items-center gap-1.5">
                          <svg className="animate-spin h-3.5 w-3.5 text-indigo-500" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"></path>
                          </svg>
                          <span>Fetching results<span className="animate-ellipsis"></span></span>
                        </span>
                      );
                    }
                    const totalDrugs = v2Results?.metadata?.total_drugs || totalResults;
                    const pageSize = v2Results?.metadata?.page_size || 50;
                    const page = v2Results?.metadata?.page || currentPage;
                    // Only show range if we're actually paginating (rendered < total)
                    if (totalDrugs > pageSize && currentResults.length < totalDrugs) {
                      const start = (page - 1) * pageSize + 1;
                      const end = Math.min(page * pageSize, totalDrugs);
                      return `Showing ${start}–${end} of ${totalDrugs} drugs`;
                    }
                    // The total lives in the "N labels" heading.
                    return null;
                    })();
                    if (progress) parts.push(<span key="progress">{progress}</span>);
                    if (!appliedIncludeSba && appliedFilters.marketingCategories.length < 9) {
                      parts.push(<span key="cats">{appliedFilters.marketingCategories.length} of 9 categories</span>);
                    }
                    if (appliedFilters.regions.length > 0 && appliedFilters.regions.length < 5) {
                      parts.push(<span key="regions">{appliedFilters.regions.map((r) => regionDisplayLabel(r, isUsFdaVisible(isAdmin))).join(', ')}</span>);
                    }
                    return parts.flatMap((part, i) => (i === 0 ? [part] : [' · ', part]));
                  })()}
                </p>
              )}

              {/* Count display for FDA Letters mode (Cogni AI letter path).
                  Parallels the drug-side count above so users get the same
                  "N results" affordance instead of nothing. Source: the
                  resolved letter hook's ``results.length``. While loading,
                  the inline spinner below already shows progress; the count
                  only appears once data is in. */}
              {isLetterMode && letterSearchQuery.data && (
                <p className="text-slate-500 text-sm mt-2">
                  {letterSearchQuery.data.results.length === 1
                    ? '1 FDA Enforcement Letter'
                    : `${letterSearchQuery.data.results.length} FDA Enforcement Letters`}
                </p>
              )}
              {!appliedIncludeComments && !appliedIncludeSba && activeResultTab === 'comments' && searchResults?.comment_results && (
                <p className="text-slate-500 text-sm mt-2">
                  {searchResults.total_comment_matches || 0} comment matches found
                </p>
              )}

              {/* Description for Draft Review Mode */}
              {appliedIncludeComments && (
                <p className="text-slate-500 text-sm mt-2">
                  Review comments, track changes, and outcomes from uploaded draft documents
                </p>
              )}
              {/* Description for SBA Review Mode */}
              {appliedIncludeSba && !hasSbaQuery && (
                <p className="text-slate-500 text-sm mt-2">
                  Search across FDA multi-disciplinary review documents
                </p>
              )}
              {/* Description for Competitive Intel Mode */}
              {appliedIncludeCi && !hasCiQuery && (
                <p className="text-slate-500 text-sm mt-2">
                  Search across tracked competitor product pages and press releases
                </p>
              )}
            </div>

            {/* Competitive Intel — one-shot document search (Task 5). */}
            {appliedIncludeCi && (
              <CiResults
                documents={ciDocuments}
                isLoading={isCiSearchLoading}
                isNoQuery={!hasCiQuery}
                totalDocuments={ciTotalDocuments}
                currentPage={currentPage}
                pageSize={CI_DOCUMENT_PAGE_SIZE}
                onPageChange={(newPage) => {
                  setCurrentPage(newPage);
                  updateUrlParams(searchQuery, appliedFilters, newPage);
                }}
              />
            )}

            {appliedIncludeApprovalLetters && (
              isApprovalLetterSearchLoading && approvalLetterResults.length === 0 ? (
                <div className="flex flex-col items-center py-12">
                  <div className="relative mb-4">
                    <div className="w-10 h-10 border-3 border-indigo-100 rounded-full"></div>
                    <div className="absolute top-0 left-0 w-10 h-10 border-3 border-indigo-500 rounded-full border-t-transparent animate-spin"></div>
                  </div>
                  <p className="text-sm font-medium text-slate-600">Searching FDA Approval Letters…</p>
                </div>
              ) : (
                <ApprovalLetterResults results={approvalLetterResults} />
              )
            )}

            {/* Drug Results - Table View (normal search mode, labels corpus only).
                Task 3 wraps this block in ``appliedFilters.searchMode === 'labels'``
                so the FDA-letters Document Type renders the letter branch below
                instead.  Draft Review / SBA modes already short-circuited above
                via ``!appliedIncludeComments && !appliedIncludeSba``; the new
                ``'labels'`` gate is the explicit drug-corpus discriminator. */}
            {isCogniDrugMode && activeResultTab === 'drugs' && !appliedIncludeComments && (
              <>
                {currentResults.length > 0 ? (
                  <>
                    <SearchResultsTable
                      results={currentResults}
                      onDrugClick={handleDrugClick}
                      showRelevanceScore={showRelevanceScore}
                      showApprovalDate={true}
                    />
                    {/* Streaming: loading more results indicator */}
                    {useStreaming && isStreamingLoading && streamingDrugCount > 0 && (
                      <div className="flex items-center gap-3 py-3 px-4 mt-2 bg-indigo-50/60 border border-indigo-100 rounded-lg">
                        <div className="relative flex-shrink-0">
                          <div className="w-5 h-5 border-2 border-indigo-200 rounded-full"></div>
                          <div className="absolute top-0 left-0 w-5 h-5 border-2 border-indigo-500 rounded-full border-t-transparent animate-spin"></div>
                        </div>
                        <span className="text-sm text-indigo-700">
                          Loading more results... ({streamingDrugCount} drugs found)
                        </span>
                      </div>
                    )}
                    {/* Pagination controls — only show when page has fewer drugs than total */}
                    {(() => {
                      const totalDrugs = v2Results?.metadata?.total_drugs || 0;
                      const pageSize = v2Results?.metadata?.page_size || 50;
                      const totalPages = Math.ceil(totalDrugs / pageSize);
                      // Hide pagination if all results are already rendered on this page
                      if (totalPages <= 1 || currentResults.length >= totalDrugs) return null;
                      return (
                        <div className="flex items-center justify-between mt-4 px-2">
                          <button
                            onClick={() => {
                              const newPage = Math.max(1, currentPage - 1);
                              setCurrentPage(newPage);
                              updateUrlParams(searchQuery, appliedFilters, newPage);
                            }}
                            disabled={currentPage <= 1}
                            className="px-3 py-1.5 text-sm font-medium rounded-lg border border-slate-200 text-slate-600 hover:bg-slate-50 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
                          >
                            Previous
                          </button>
                          <span className="text-sm text-slate-500">
                            Page {currentPage} of {totalPages}
                          </span>
                          <button
                            onClick={() => {
                              const newPage = Math.min(totalPages, currentPage + 1);
                              setCurrentPage(newPage);
                              updateUrlParams(searchQuery, appliedFilters, newPage);
                            }}
                            disabled={currentPage >= totalPages}
                            className="px-3 py-1.5 text-sm font-medium rounded-lg border border-slate-200 text-slate-600 hover:bg-slate-50 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
                          >
                            Next
                          </button>
                        </div>
                      );
                    })()}
                  </>
                ) : useStreaming && isStreamingLoading ? (
                  /* Streaming active but no high-confidence results yet — show compact loading */
                  <div className="flex flex-col items-center py-12">
                    <div className="relative mb-4">
                      <div className="w-10 h-10 border-3 border-indigo-100 rounded-full"></div>
                      <div className="absolute top-0 left-0 w-10 h-10 border-3 border-indigo-500 rounded-full border-t-transparent animate-spin"></div>
                    </div>
                    <p className="text-sm font-medium text-slate-600">Ranking candidates<span className="animate-ellipsis"></span></p>
                    <p className="text-xs text-slate-400 mt-1">High-confidence results will appear shortly</p>
                  </div>
                ) : (
                  <div className="text-center py-16">
                    <div className="inline-flex items-center justify-center w-16 h-16 bg-slate-100 rounded-full mb-4">
                      <span className="text-2xl">🔍</span>
                    </div>
                    <p className="text-lg text-slate-600">No results found</p>
                    <p className="text-sm text-slate-400 mt-1">
                      Try adjusting your search terms
                    </p>
                  </div>
                )}
              </>
            )}

            {/* Label Negotiations block — additive draft-corpus results rendered
                alongside the normal drug results when includeLabelNegotiations
                is enabled. Only shown in labels mode (isNormalSearchMode). */}
            {isNormalSearchMode && includeLabelNegotiations && v2Results?.draft_results && v2Results.draft_results.length > 0 && (
              <div className="mt-6">
                <div className="flex items-center justify-between mb-3">
                  <h3 className="text-base font-semibold text-slate-800">
                    Label Negotiations
                    <span className="ml-2 text-xs font-normal text-slate-500 bg-slate-100 px-2 py-0.5 rounded-full">
                      {v2Results.draft_results.length} draft{v2Results.draft_results.length !== 1 ? 's' : ''}
                    </span>
                  </h3>
                  {/* Comments toggle — gated to authenticated users (label-negotiations is authenticated-gated).
                      Two states matching the backend's ``draft_comment_scope``
                      vocabulary: 'all' (draft content + comments) and
                      'comments_only'. */}
                  {isAuthenticated && (
                    <div className="flex items-center gap-1 text-xs">
                      {([
                        { scope: 'all', label: 'Draft + comments' },
                        { scope: 'comments_only', label: 'Comments only' },
                      ] as const).map(({ scope, label }) => (
                        <button
                          key={scope}
                          onClick={() => setDraftCommentScope(scope)}
                          className={`px-2 py-1 rounded-md transition-colors ${
                            draftCommentScope === scope
                              ? 'bg-indigo-600 text-white'
                              : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
                          }`}
                        >
                          {label}
                        </button>
                      ))}
                    </div>
                  )}
                </div>
                <div className="space-y-3">
                  {v2Results.draft_results.map((draftResult) =>
                    // Backend already scopes the hits per ``draft_comment_scope``
                    // (the React Query / streaming key includes it, so toggling
                    // re-queries); render everything it returns.
                    draftResult.hits
                      .map((hit, hi) => (
                        <div
                          key={`draft-${draftResult.label_version_id}-${hi}`}
                          onClick={() => handleDraftHitClick(draftResult, hit)}
                          className="bg-white border border-slate-200/60 rounded-xl p-4 hover:border-indigo-200 hover:shadow-sm transition-all cursor-pointer"
                        >
                          <div className="flex items-center gap-2 mb-2">
                            <span className={`px-1.5 py-0.5 text-[10px] font-medium rounded ${
                              hit.kind === 'comment'
                                ? 'bg-purple-100 text-purple-700'
                                : 'bg-blue-100 text-blue-700'
                            }`}>
                              {hit.kind}
                            </span>
                            <span className="text-sm font-medium text-slate-800">
                              {draftResult.document_name}
                            </span>
                            {draftResult.drug_name && (
                              <span className="text-xs text-slate-500">{draftResult.drug_name}</span>
                            )}
                          </div>
                          <p className="text-sm text-slate-700 leading-relaxed">{hit.snippet}</p>
                        </div>
                      ))
                  )}
                </div>
              </div>
            )}

            {/* FDA Letter Results — Cogni AI letter search path (Task 3).
                Renders the ``LetterResultsTable`` when ``letterSearchQuery``
                resolves with data, or an inline "Searching FDA letters…"
                spinner while the request is in flight.  ``LetterResultsTable``
                itself owns the empty-results state ("No matching FDA letters
                found.").  The spinner shape mirrors the drug compact spinner
                above (no full-page spinner in letter mode — letters aren't
                streamed). */}
            {isLetterMode && (
              letterSearchQuery.isLoading && !letterSearchQuery.data ? (
                <div className="flex flex-col items-center py-12">
                  <div className="relative mb-4">
                    <div className="w-10 h-10 border-3 border-indigo-100 rounded-full"></div>
                    <div className="absolute top-0 left-0 w-10 h-10 border-3 border-indigo-500 rounded-full border-t-transparent animate-spin"></div>
                  </div>
                  <p className="text-sm font-medium text-slate-600">Searching FDA Enforcement Letters…</p>
                </div>
              ) : letterSearchQuery.data ? (
                <>
                  {letterSearchQuery.data.notice && (
                    <div className="mb-2 rounded-md bg-amber-50 border border-amber-200 px-3 py-2 text-sm text-amber-800">
                      {letterSearchQuery.data.notice}
                    </div>
                  )}
                  <LetterResultsTable results={letterSearchQuery.data.results} />
                </>
              ) : null
            )}

            {/* Draft Review Mode - 7-column table */}
            {appliedIncludeComments && (
              <>
                {/* Show intent when using intelligent search */}
                {hasSearchQueryForDraft && draftReviewData?.intent && (
                  <div className="mb-6 p-4 bg-gradient-to-r from-purple-50 to-indigo-50 border border-purple-100/50 rounded-xl">
                    <div className="flex items-start gap-3">
                      <span className="text-lg">🔍</span>
                      <div className="flex-1">
                        <div className="flex items-center gap-2 mb-1">
                          <p className="text-xs font-semibold text-purple-600 uppercase tracking-wide">
                            Search Intent
                          </p>
                          <span className="px-2 py-0.5 text-xs bg-slate-100 text-slate-600 rounded-full">
                            {draftReviewData.processing_time_ms}ms
                          </span>
                        </div>
                        <p className="text-slate-700">
                          {draftReviewData.intent.intent_description}
                        </p>
                        {/* Show inferred filters */}
                        {(draftReviewData.intent.inferred_edit_type || draftReviewData.intent.inferred_outcome_status) && (
                          <div className="flex gap-2 mt-2">
                            {draftReviewData.intent.inferred_edit_type && (
                              <span className="px-2 py-0.5 text-xs bg-blue-100 text-blue-700 rounded-full">
                                Inferred: {draftReviewData.intent.inferred_edit_type}
                              </span>
                            )}
                            {draftReviewData.intent.inferred_outcome_status && (
                              <span className="px-2 py-0.5 text-xs bg-green-100 text-green-700 rounded-full">
                                Inferred: {draftReviewData.intent.inferred_outcome_status}
                              </span>
                            )}
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                )}

                {/* Results count */}
                <p className="text-slate-500 text-sm mb-4">
                  {hasSearchQueryForDraft
                    ? `${draftReviewData?.total || 0} results found`
                    : `${draftCommentsData?.total || 0} items`
                  }
                </p>

                <DraftCommentsTable
                  results={draftResults}
                  isLoading={isDraftLoading}
                  onRefresh={() => handleDraftRefetch()}
                />
              </>
            )}

            {/* Comment Results (normal search mode only) */}
            {!appliedIncludeComments && !appliedIncludeSba && activeResultTab === 'comments' && searchResults?.comment_results && (
              <div className="space-y-3">
                {searchResults.comment_results.length > 0 ? (
                  searchResults.comment_results.map((comment: CommentSearchResult) => (
                    <div
                      key={comment.comment_id}
                      onClick={() => handleCommentClick(comment)}
                      className="group bg-white p-4 rounded-xl border border-slate-200/60 hover:border-indigo-200 hover:shadow-md transition-all duration-200 cursor-pointer"
                    >
                      {/* Drug name header - prominent display */}
                      {comment.brand_name && (
                        <div className="flex items-center gap-2 mb-3 pb-2 border-b border-slate-100">
                          <span className="text-base font-semibold text-indigo-700 group-hover:text-indigo-800">
                            {comment.brand_name}
                          </span>
                          <span className="text-xs text-slate-400">
                            v{comment.version_number}
                            {comment.version_status && ` (${comment.version_status})`}
                          </span>
                        </div>
                      )}

                      <div className="flex items-center gap-2 flex-wrap mb-2">
                        {comment.comment_type && (
                          <span className="px-2 py-0.5 text-xs font-medium rounded-full bg-slate-100 text-slate-700">
                            {comment.comment_type}
                          </span>
                        )}
                        {comment.author && (
                          <span className="text-sm font-medium text-slate-700">
                            {comment.author}
                          </span>
                        )}
                        {!comment.brand_name && (
                          <span className="text-xs text-slate-400">
                            v{comment.version_number}
                            {comment.version_status && ` (${comment.version_status})`}
                          </span>
                        )}
                      </div>

                      <p className="text-slate-800 text-sm leading-relaxed mb-2">
                        {comment.comment_text}
                      </p>

                      {/* Full anchor text (context) */}
                      {comment.context_text && (
                        <div className="p-3 bg-amber-50 rounded-lg border border-amber-200 mb-3">
                          <p className="text-xs text-amber-700 font-medium mb-1">
                            Anchor text{comment.context_section_type && ` (${comment.context_section_type})`}:
                          </p>
                          <p className="text-sm text-slate-700 italic">
                            "{comment.context_text}"
                          </p>
                        </div>
                      )}

                      {/* Full thread replies */}
                      {comment.thread_replies && comment.thread_replies.length > 0 && (
                        <div className="mt-3 space-y-2">
                          <p className="text-xs text-slate-500 font-semibold uppercase tracking-wide">
                            Thread ({comment.thread_replies.length} {comment.thread_replies.length === 1 ? 'reply' : 'replies'}):
                          </p>
                          {comment.thread_replies.map((reply) => (
                            <div key={reply.comment_id} className="pl-3 border-l-2 border-indigo-200 bg-slate-50 rounded-r p-2">
                              <p className="text-xs font-medium text-indigo-700 mb-0.5">
                                {reply.author || 'Reply'}
                              </p>
                              <p className="text-sm text-slate-700">
                                {reply.comment_text}
                              </p>
                            </div>
                          ))}
                        </div>
                      )}

                      {comment.user_note && (
                        <div className="mt-2 p-2 bg-indigo-50 rounded">
                          <p className="text-xs font-medium text-indigo-700">Note:</p>
                          <p className="text-xs text-indigo-900">{comment.user_note}</p>
                        </div>
                      )}
                    </div>
                  ))
                ) : (
                  <div className="text-center py-16">
                    <div className="inline-flex items-center justify-center w-16 h-16 bg-slate-100 rounded-full mb-4">
                      <span className="text-2xl">💬</span>
                    </div>
                    <p className="text-lg text-slate-600">No comment matches found</p>
                    <p className="text-sm text-slate-400 mt-1">
                      Try different search terms or check drug labels instead
                    </p>
                  </div>
                )}
              </div>
            )}

          </div>
        )}

            </div>
          </div>
          </div>
        </div>
      ) : null}
    </div>
  );
};

export default SearchPage;
